// ============================================================================
// Módulo INFORMES — batería de reportes gerenciales por proceso.
// Cada informe se define de forma declarativa: filtros + calc() que devuelve
// { kpis, columnas, filas, nota } y el framework pinta KPIs, tabla y exporta
// a CSV con un solo motor. Los PDF (desprendibles/certificados) viven en
// src/export/pdf.js y se disparan con acciones por fila.
// ============================================================================
import { anticipoAplicado, anticipoDisponible, getCostoPromedio, getStockProducto } from "../core/accounting.js";
import { desgloseIVA } from "../core/iva.js";
import { valorRemision } from "../core/remisionValor.js";
import { BRAND, C, ESTADOS } from "../core/config.js";
import { state } from "../core/state.js";
import { btnStyle, diasPagoDe, escapeHTML, fmtCOP, fmtDate, inputStyle, parseNumber, today } from "../core/utils.js";
import { cargarUsoDiario } from "../core/usoTracker.js";
import { csvDescargar } from "../export/excelCsv.js";
import { exportCertIngresosRetenciones, exportCertificadoLaboral, exportDesprendibleNomina } from "../export/pdf.js";
import { cargarAuditoria } from "./auditoria.js";
import { calcularEstadoResultados, getSaldoCuentaRango } from "./contabilidad.js";
import { getDescuentosFinancieros, getLiquidacionOP, getSaldoOCP } from "./documentos.js";
import { getSaldoFactura, getTotalNC } from "./facturas.js";

const NAVY = C.mathiz;

// ── Utilidades de fechas y agrupación ──────────────────────────────────────
function rangoDefault() {
  const hoy = today();
  return { desde: hoy.slice(0, 4) + "-01-01", hasta: hoy };
}

function enRango(fecha, f) {
  if (!fecha) return false;
  if (f.desde && fecha < f.desde) return false;
  if (f.hasta && fecha > f.hasta) return false;
  return true;
}

function diasEntre(desde, hasta) {
  try {
    return Math.round((new Date(hasta + "T12:00:00") - new Date(desde + "T12:00:00")) / 86400000);
  } catch { return 0; }
}

const mesDe = fecha => (fecha || "").slice(0, 7);

function nombreMes(ym) {
  const [y, m] = ym.split("-");
  const nombres = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${nombres[parseInt(m, 10) - 1]} ${y}`;
}

const nombreCliente = id => ((state.data.clientes || []).find(c => c.id === id) || {}).nombre || "Sin cliente";
const nombreProveedor = id => ((state.data.proveedores || []).find(p => p.id === id) || {}).nombre || "Sin proveedor";

function pct(n, d) { return d > 0 ? (n / d) * 100 : 0; }

// ── Formateadores de celdas (pantalla y CSV) ───────────────────────────────
const FMT = {
  cop:   { render: v => fmtCOP(v), csv: v => Math.round(v || 0), align: "right" },
  num:   { render: v => (v == null ? "—" : String(Math.round(v * 100) / 100)), csv: v => v == null ? "" : Math.round(v * 100) / 100, align: "right" },
  pct:   { render: v => (v == null ? "—" : v.toFixed(1) + "%"), csv: v => v == null ? "" : v.toFixed(1), align: "right" },
  fecha: { render: v => (v ? fmtDate(v) : "—"), csv: v => v || "", align: "left" },
  texto: { render: v => escapeHTML(v == null ? "" : String(v)) || "—", csv: v => v == null ? "" : String(v), align: "left" },
  dias:  { render: v => (v == null ? "—" : `${v} día${Math.abs(v) === 1 ? "" : "s"}`), csv: v => v == null ? "" : v, align: "right" },
  html:  { render: v => v == null ? "—" : String(v), csv: v => "", align: "center" }
};

// ============================================================================
// CATEGORÍAS
// ============================================================================
const CATEGORIAS = [
  { id: "comercial",    label: "Comercial (Cotizaciones)", icon: "📋", color: C.blue },
  { id: "produccion",   label: "Producción (OP)",          icon: "🔧", color: C.amber },
  { id: "compras",      label: "Compras (OCP)",            icon: "🛒", color: C.purple },
  { id: "ventas",       label: "Ventas y Cartera",         icon: "💰", color: "#059669" },
  { id: "tesoreria",    label: "Tesorería",                icon: "💵", color: "#0E7490" },
  { id: "inventario",   label: "Inventario",               icon: "📦", color: "#f59e0b" },
  { id: "contabilidad", label: "Contabilidad",             icon: "🧮", color: "#0E7490" },
  { id: "nomina",       label: "Nómina y Empleados",       icon: "🧑‍🌾", color: "#4C5FA8" },
  { id: "activos",      label: "Activos Fijos",            icon: "🏢", color: "#334155" },
  { id: "control",      label: "Comisiones y Control",     icon: "🕵️", color: "#0f172a" }
];

// ============================================================================
// INFORMES — Comercial (CT)
// ============================================================================
function vendedoresOpciones() {
  const set = new Set();
  (state.data.cotizaciones || []).forEach(ct => { if (ct.asesor_comercial) set.add(ct.asesor_comercial); });
  (state.data.pedidos || []).forEach(op => { if (op.asesor_comercial) set.add(op.asesor_comercial); });
  return [...set].sort().map(v => [v, v]);
}

const INF_COMERCIAL = [
  {
    id: "embudo-ct", cat: "comercial", icon: "🔻", titulo: "Embudo de conversión CT → OP",
    desc: "Tasa de cierre, valor cotizado vs vendido y días promedio de conversión, por vendedor.",
    filtros: [{ key: "vendedor", label: "Vendedor", tipo: "select", opciones: vendedoresOpciones }],
    calc(f) {
      const cts = (state.data.cotizaciones || []).filter(ct => enRango(ct.fecha, f))
        .filter(ct => !f.vendedor || ct.asesor_comercial === f.vendedor);
      const porVend = {};
      cts.forEach(ct => {
        const v = ct.asesor_comercial || "Sin asesor";
        if (!porVend[v]) porVend[v] = { vendedor: v, n: 0, valor: 0, aprobadas: 0, convertidas: 0, valorConv: 0, diasConv: [] };
        const g = porVend[v];
        g.n++; g.valor += ct.total || 0;
        if (["aprobada", "completada", "convertida"].includes(ct.estado)) g.aprobadas++;
        const hijosOP = [].concat(ct._convertedTo || []).filter(h => h && h.tipo === "OP");
        if (ct.estado === "convertida" || hijosOP.length > 0) {
          g.convertidas++; g.valorConv += ct.total || 0;
          if (hijosOP[0] && hijosOP[0].fecha) g.diasConv.push(Math.max(0, diasEntre(ct.fecha, hijosOP[0].fecha)));
        }
      });
      const filas = Object.values(porVend).map(g => ({
        vendedor: g.vendedor, cotizadas: g.n, valorCotizado: g.valor,
        aprobadas: g.aprobadas, convertidas: g.convertidas, valorVendido: g.valorConv,
        tasa: pct(g.convertidas, g.n),
        diasProm: g.diasConv.length ? g.diasConv.reduce((s, d) => s + d, 0) / g.diasConv.length : null
      })).sort((a, b) => b.valorVendido - a.valorVendido);
      const tot = filas.reduce((s, r) => ({ n: s.n + r.cotizadas, v: s.v + r.valorCotizado, c: s.c + r.convertidas, vc: s.vc + r.valorVendido }), { n: 0, v: 0, c: 0, vc: 0 });
      return {
        kpis: [
          { label: "Cotizaciones", valor: String(tot.n) },
          { label: "Valor cotizado", valor: fmtCOP(tot.v) },
          { label: "Convertidas a OP", valor: `${tot.c} (${pct(tot.c, tot.n).toFixed(1)}%)`, color: tot.n && pct(tot.c, tot.n) >= 30 ? "#059669" : C.amber },
          { label: "Valor vendido", valor: fmtCOP(tot.vc), color: "#059669" }
        ],
        columnas: [
          { key: "vendedor", label: "Vendedor", fmt: "texto" },
          { key: "cotizadas", label: "Cotizadas", fmt: "num" },
          { key: "valorCotizado", label: "Valor cotizado", fmt: "cop" },
          { key: "aprobadas", label: "Aprobadas", fmt: "num" },
          { key: "convertidas", label: "Convertidas", fmt: "num" },
          { key: "valorVendido", label: "Valor vendido", fmt: "cop" },
          { key: "tasa", label: "Tasa de cierre", fmt: "pct" },
          { key: "diasProm", label: "Días prom. cierre", fmt: "num" }
        ],
        filas
      };
    }
  },
  {
    id: "ct-perdidas", cat: "comercial", icon: "❌", titulo: "Cotizaciones perdidas",
    desc: "Rechazadas y vencidas sin convertir: cuánto valor se dejó de vender y de quién era.",
    filtros: [{ key: "vendedor", label: "Vendedor", tipo: "select", opciones: vendedoresOpciones }],
    calc(f) {
      const hoy = today();
      const filas = (state.data.cotizaciones || []).filter(ct => enRango(ct.fecha, f))
        .filter(ct => !f.vendedor || ct.asesor_comercial === f.vendedor)
        .filter(ct => ct.estado === "rechazada" || (["borrador", "enviada"].includes(ct.estado) && ct.fecha_vigencia && ct.fecha_vigencia < hoy))
        .map(ct => ({
          numero: ct.numero, fecha: ct.fecha, cliente: nombreCliente(ct.cliente_id),
          vendedor: ct.asesor_comercial || "—",
          motivo: ct.estado === "rechazada" ? "Rechazada" : "Vencida sin respuesta",
          diasDesde: diasEntre(ct.fecha, hoy), total: ct.total || 0
        })).sort((a, b) => b.total - a.total);
      const total = filas.reduce((s, r) => s + r.total, 0);
      return {
        kpis: [
          { label: "CT perdidas", valor: String(filas.length), color: C.red },
          { label: "Valor perdido", valor: fmtCOP(total), color: C.red },
          { label: "Rechazadas", valor: String(filas.filter(r => r.motivo === "Rechazada").length) },
          { label: "Vencidas sin respuesta", valor: String(filas.filter(r => r.motivo !== "Rechazada").length) }
        ],
        columnas: [
          { key: "numero", label: "Cotización", fmt: "texto" },
          { key: "fecha", label: "Fecha", fmt: "fecha" },
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "vendedor", label: "Vendedor", fmt: "texto" },
          { key: "motivo", label: "Motivo", fmt: "texto" },
          { key: "diasDesde", label: "Días desde emisión", fmt: "dias" },
          { key: "total", label: "Valor", fmt: "cop" }
        ],
        filas
      };
    }
  },
  {
    id: "ct-seguimiento", cat: "comercial", icon: "📤", titulo: "Cotizaciones enviadas sin respuesta",
    desc: "CT en estado 'enviada' con más de N días sin conversión — la lista de llamadas pendientes.",
    filtros: [{ key: "diasMin", label: "Mínimo días sin respuesta", tipo: "number", default: 3 }],
    calc(f) {
      const hoy = today();
      const umbral = parseNumber(f.diasMin) || 0;
      const filas = (state.data.cotizaciones || [])
        .filter(ct => ct.estado === "enviada")
        .map(ct => {
          const envio = ct._fecha_enviada || ct.fecha;
          return {
            numero: ct.numero, cliente: nombreCliente(ct.cliente_id), vendedor: ct.asesor_comercial || "—",
            fechaEnvio: envio, dias: diasEntre(envio, hoy),
            contacto: ct._contacto ? [ct._contacto.nombre, ct._contacto.telefono].filter(Boolean).join(" · ") : "—",
            total: ct.total || 0
          };
        })
        .filter(r => r.dias >= umbral)
        .sort((a, b) => b.dias - a.dias);
      return {
        kpis: [
          { label: "CT sin respuesta", valor: String(filas.length), color: filas.length ? C.amber : "#059669" },
          { label: "Valor en juego", valor: fmtCOP(filas.reduce((s, r) => s + r.total, 0)) },
          { label: "Más antigua", valor: filas.length ? `${filas[0].dias} días` : "—" }
        ],
        columnas: [
          { key: "numero", label: "Cotización", fmt: "texto" },
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "vendedor", label: "Vendedor", fmt: "texto" },
          { key: "fechaEnvio", label: "Enviada", fmt: "fecha" },
          { key: "dias", label: "Días sin respuesta", fmt: "dias" },
          { key: "contacto", label: "Contacto", fmt: "texto" },
          { key: "total", label: "Valor", fmt: "cop" }
        ],
        filas,
        nota: "El reloj corre desde la fecha en que la cotización pasó a estado \"enviada\" (o su fecha de emisión si no se registró el envío)."
      };
    }
  }
];

// ============================================================================
// INFORMES — Producción (OP)
// ============================================================================
const INF_PRODUCCION = [
  {
    id: "op-desviacion", cat: "produccion", icon: "📉", titulo: "Desviación de margen por OP",
    desc: "Margen estimado al cotizar vs margen real liquidado — dónde se comió el sobrecosto.",
    filtros: [],
    calc(f) {
      const filas = (state.data.pedidos || []).filter(op => enRango(op.fecha, f) && op.estado !== "cancelada")
        .map(op => {
          const liq = getLiquidacionOP(op.id);
          const mi = liq.margenInicial, mf = liq.margen;
          const desv = (mi != null && mf != null) ? mi - mf : null;
          return {
            numero: op.numero, fecha: op.fecha, cliente: nombreCliente(op.cliente_id),
            venta: liq.ventaTotal || 0, costo: liq.costoTotal || 0,
            margenInicial: mi, margenFinal: mf, desviacion: desv,
            sobrecosto: desv != null && desv > 0 ? Math.round((liq.ventaTotal || 0) * desv / 100) : 0
          };
        })
        .sort((a, b) => (b.desviacion || 0) - (a.desviacion || 0));
      const criticas = filas.filter(r => (r.desviacion || 0) > 5);
      return {
        kpis: [
          { label: "OPs analizadas", valor: String(filas.length) },
          { label: "Con desviación > 5 pts", valor: String(criticas.length), color: criticas.length ? C.red : "#059669" },
          { label: "Sobrecosto total", valor: fmtCOP(filas.reduce((s, r) => s + r.sobrecosto, 0)), color: C.red }
        ],
        columnas: [
          { key: "numero", label: "OP", fmt: "texto" },
          { key: "fecha", label: "Fecha", fmt: "fecha" },
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "venta", label: "Venta", fmt: "cop" },
          { key: "costo", label: "Costo real", fmt: "cop" },
          { key: "margenInicial", label: "Margen inicial", fmt: "pct" },
          { key: "margenFinal", label: "Margen final", fmt: "pct" },
          { key: "desviacion", label: "Desviación (pts)", fmt: "num" },
          { key: "sobrecosto", label: "Sobrecosto", fmt: "cop" }
        ],
        filas,
        nota: "Desviación positiva = la OP terminó con menos margen del estimado al cotizar. El sobrecosto aproxima cuánta utilidad se perdió (venta × puntos de desviación)."
      };
    }
  },
  {
    id: "op-entregas", cat: "produccion", icon: "⏱️", titulo: "Cumplimiento de fechas de entrega",
    desc: "Fecha comprometida de cada OP vs primera remisión real: % de entregas a tiempo.",
    filtros: [],
    calc(f) {
      const hoy = today();
      const filas = (state.data.pedidos || [])
        .filter(op => enRango(op.fecha, f) && op.estado !== "cancelada")
        .map(op => {
          const comprometida = (op._entregas || []).map(e => e.fecha).filter(Boolean).sort()[0] || null;
          const rcs = (state.data.remisiones || []).filter(rc => rc._origen && rc._origen.id === op.id && rc.estado !== "cancelada");
          const entregaReal = rcs.map(rc => rc.fecha).filter(Boolean).sort()[0] || null;
          let estado, atraso = null;
          if (!comprometida) estado = "Sin fecha comprometida";
          else if (entregaReal) {
            atraso = diasEntre(comprometida, entregaReal);
            estado = atraso <= 0 ? "✅ A tiempo" : `🔴 Tarde`;
          } else {
            estado = comprometida < hoy ? "⚠️ Vencida sin entrega" : "⏳ En curso";
          }
          return { numero: op.numero, cliente: nombreCliente(op.cliente_id), comprometida, entregaReal, atraso: atraso != null && atraso > 0 ? atraso : (atraso != null ? 0 : null), estado, total: op.total || 0 };
        })
        .filter(r => r.comprometida || r.entregaReal)
        .sort((a, b) => (b.atraso || 0) - (a.atraso || 0));
      const evaluadas = filas.filter(r => r.entregaReal && r.comprometida);
      const aTiempo = evaluadas.filter(r => r.atraso === 0).length;
      return {
        kpis: [
          { label: "Entregas evaluadas", valor: String(evaluadas.length) },
          { label: "A tiempo", valor: evaluadas.length ? `${aTiempo} (${pct(aTiempo, evaluadas.length).toFixed(0)}%)` : "—", color: pct(aTiempo, evaluadas.length) >= 80 ? "#059669" : C.amber },
          { label: "Vencidas sin entregar", valor: String(filas.filter(r => r.estado === "⚠️ Vencida sin entrega").length), color: C.red }
        ],
        columnas: [
          { key: "numero", label: "OP", fmt: "texto" },
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "comprometida", label: "Comprometida", fmt: "fecha" },
          { key: "entregaReal", label: "Primera remisión", fmt: "fecha" },
          { key: "atraso", label: "Atraso", fmt: "dias" },
          { key: "estado", label: "Estado", fmt: "texto" },
          { key: "total", label: "Valor OP", fmt: "cop" }
        ],
        filas
      };
    }
  },
  {
    id: "op-aging", cat: "produccion", icon: "⏳", titulo: "Órdenes de producción envejecidas",
    desc: "OPs abiertas ordenadas por días sin completar — detecta producción estancada.",
    filtros: [],
    calc() {
      const hoy = today();
      const filas = (state.data.pedidos || [])
        .filter(op => ["borrador", "confirmada", "en_proceso"].includes(op.estado))
        .map(op => ({
          numero: op.numero, fecha: op.fecha, cliente: nombreCliente(op.cliente_id),
          estado: (ESTADOS.OP || {})[op.estado] || op.estado,
          dias: diasEntre(op.fecha, hoy), total: op.total || 0
        }))
        .sort((a, b) => b.dias - a.dias);
      const banda = lim => filas.filter(r => r.dias > lim).length;
      return {
        kpis: [
          { label: "OPs abiertas", valor: String(filas.length) },
          { label: "Valor en curso", valor: fmtCOP(filas.reduce((s, r) => s + r.total, 0)) },
          { label: "Más de 15 días", valor: String(banda(15)), color: C.amber },
          { label: "Más de 30 días", valor: String(banda(30)), color: C.red }
        ],
        columnas: [
          { key: "numero", label: "OP", fmt: "texto" },
          { key: "fecha", label: "Fecha", fmt: "fecha" },
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "estado", label: "Estado", fmt: "texto" },
          { key: "dias", label: "Días abierta", fmt: "dias" },
          { key: "total", label: "Valor", fmt: "cop" }
        ],
        filas
      };
    }
  },
  {
    id: "op-costos-concepto", cat: "produccion", icon: "🧾", titulo: "Costos de producción por concepto",
    desc: "En qué se va el costo: compras a proveedor (OCP) y costos asociados, agregados por concepto.",
    filtros: [],
    calc(f) {
      const opsRango = (state.data.pedidos || []).filter(op => enRango(op.fecha, f) && op.estado !== "cancelada");
      const opIds = new Set(opsRango.map(op => op.id));
      const grupos = {};
      const add = (clave, tipo, monto, opId) => {
        if (!grupos[clave]) grupos[clave] = { concepto: clave, tipo, total: 0, ops: new Set() };
        grupos[clave].total += monto;
        grupos[clave].ops.add(opId);
      };
      (state.data.ocp || []).forEach(o => {
        if (o.estado === "cancelada" || !o._origen || !opIds.has(o._origen.id)) return;
        add(`Compras — ${nombreProveedor(o.proveedor_id)}`, "OCP", o.total || 0, o._origen.id);
      });
      opsRango.forEach(op => {
        (op._conceptosCosto || []).forEach(cc => {
          const monto = (parseNumber(cc.cantidad) || 0) * (parseNumber(cc.valor_tercero) || 0);
          if (monto <= 0) return;
          const nombre = cc._esComision ? "Comisiones de venta" : (cc.concepto || "Otros costos");
          add(nombre, "Concepto", monto, op.id);
        });
      });
      const filas = Object.values(grupos)
        .map(g => ({ concepto: g.concepto, tipo: g.tipo, nOps: g.ops.size, total: g.total }))
        .sort((a, b) => b.total - a.total);
      const total = filas.reduce((s, r) => s + r.total, 0);
      filas.forEach(r => { r.participacion = pct(r.total, total); });
      return {
        kpis: [
          { label: "Costo total del período", valor: fmtCOP(total) },
          { label: "Conceptos distintos", valor: String(filas.length) },
          { label: "Mayor rubro", valor: filas[0] ? `${filas[0].concepto} (${filas[0].participacion.toFixed(0)}%)` : "—" }
        ],
        columnas: [
          { key: "concepto", label: "Concepto / Proveedor", fmt: "texto" },
          { key: "tipo", label: "Tipo", fmt: "texto" },
          { key: "nOps", label: "OPs", fmt: "num" },
          { key: "total", label: "Costo total", fmt: "cop" },
          { key: "participacion", label: "Participación", fmt: "pct" }
        ],
        filas
      };
    }
  }
];

// ============================================================================
// INFORMES — Compras (OCP / Proveedores)
// ============================================================================
const INF_COMPRAS = [
  {
    id: "compras-proveedor", cat: "compras", icon: "🏭", titulo: "Compras por proveedor",
    desc: "Volumen, participación y ticket promedio de cada proveedor en el período.",
    filtros: [],
    calc(f) {
      const ocps = (state.data.ocp || []).filter(o => enRango(o.fecha, f) && o.estado !== "cancelada");
      const grupos = {};
      ocps.forEach(o => {
        const pid = o.proveedor_id || "sin";
        if (!grupos[pid]) grupos[pid] = { proveedor: nombreProveedor(o.proveedor_id), n: 0, total: 0 };
        grupos[pid].n++; grupos[pid].total += o.total || 0;
      });
      const total = Object.values(grupos).reduce((s, g) => s + g.total, 0);
      const filas = Object.values(grupos).map(g => ({
        proveedor: g.proveedor, ordenes: g.n, total: g.total,
        ticket: g.n ? g.total / g.n : 0, participacion: pct(g.total, total)
      })).sort((a, b) => b.total - a.total);
      return {
        kpis: [
          { label: "Total comprado", valor: fmtCOP(total) },
          { label: "Órdenes", valor: String(ocps.length) },
          { label: "Proveedores activos", valor: String(filas.length) },
          { label: "Proveedor principal", valor: filas[0] ? `${filas[0].proveedor} (${filas[0].participacion.toFixed(0)}%)` : "—" }
        ],
        columnas: [
          { key: "proveedor", label: "Proveedor", fmt: "texto" },
          { key: "ordenes", label: "Órdenes", fmt: "num" },
          { key: "total", label: "Total comprado", fmt: "cop" },
          { key: "ticket", label: "Ticket promedio", fmt: "cop" },
          { key: "participacion", label: "Participación", fmt: "pct" }
        ],
        filas
      };
    }
  },
  {
    id: "compras-precios", cat: "compras", icon: "📈", titulo: "Evolución de precios de compra",
    desc: "Último precio pagado por artículo vs su promedio histórico — para negociar con datos.",
    filtros: [],
    calc(f) {
      const porArticulo = {};
      (state.data.ocp || []).filter(o => o.estado !== "cancelada").forEach(o => {
        (o.items || []).forEach(it => {
          if (!it.articulo || !parseNumber(it.precio_unitario)) return;
          if (!porArticulo[it.articulo]) porArticulo[it.articulo] = [];
          porArticulo[it.articulo].push({ fecha: o.fecha, proveedor: nombreProveedor(o.proveedor_id), precio: parseNumber(it.precio_unitario) });
        });
      });
      const filas = Object.entries(porArticulo).map(([articulo, compras]) => {
        compras.sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
        const ultima = compras[compras.length - 1];
        if (f.desde && ultima.fecha < f.desde) return null;
        const previas = compras.slice(0, -1);
        const promPrevio = previas.length ? previas.reduce((s, c) => s + c.precio, 0) / previas.length : null;
        return {
          articulo, compras: compras.length,
          fechaUltima: ultima.fecha, proveedorUltimo: ultima.proveedor, precioUltimo: ultima.precio,
          promedioPrevio: promPrevio,
          variacion: promPrevio ? ((ultima.precio - promPrevio) / promPrevio) * 100 : null
        };
      }).filter(Boolean).sort((a, b) => (b.variacion || -Infinity) - (a.variacion || -Infinity));
      const subieron = filas.filter(r => (r.variacion || 0) > 3).length;
      return {
        kpis: [
          { label: "Artículos comprados", valor: String(filas.length) },
          { label: "Con alza > 3%", valor: String(subieron), color: subieron ? C.red : "#059669" },
          { label: "Con baja de precio", valor: String(filas.filter(r => (r.variacion || 0) < -0.5).length), color: "#059669" }
        ],
        columnas: [
          { key: "articulo", label: "Artículo", fmt: "texto" },
          { key: "compras", label: "Compras", fmt: "num" },
          { key: "fechaUltima", label: "Última compra", fmt: "fecha" },
          { key: "proveedorUltimo", label: "Proveedor", fmt: "texto" },
          { key: "precioUltimo", label: "Último precio", fmt: "cop" },
          { key: "promedioPrevio", label: "Promedio anterior", fmt: "cop" },
          { key: "variacion", label: "Variación", fmt: "pct" }
        ],
        filas,
        nota: "El filtro \"Desde\" limita a artículos cuya última compra cae en el rango. La variación compara el último precio contra el promedio de todas las compras anteriores."
      };
    }
  },
  {
    id: "compras-cumplimiento", cat: "compras", icon: "🚚", titulo: "Cumplimiento de proveedores",
    desc: "Fecha de entrega prometida vs recepción real de mercancía, por proveedor.",
    filtros: [],
    calc(f) {
      const hoy = today();
      const porProv = {};
      (state.data.ocp || []).filter(o => enRango(o.fecha, f) && o.estado !== "cancelada" && o.fecha_entrega).forEach(o => {
        const pid = o.proveedor_id || "sin";
        if (!porProv[pid]) porProv[pid] = { proveedor: nombreProveedor(o.proveedor_id), evaluadas: 0, aTiempo: 0, tarde: 0, atrasos: [], pendientesVencidas: 0 };
        const g = porProv[pid];
        const recepcion = (o._recepciones || []).map(r => r.fecha).filter(Boolean).sort()[0] || null;
        if (recepcion) {
          g.evaluadas++;
          const atraso = diasEntre(o.fecha_entrega, recepcion);
          if (atraso <= 0) g.aTiempo++; else { g.tarde++; g.atrasos.push(atraso); }
        } else if (o.fecha_entrega < hoy) {
          g.pendientesVencidas++;
        }
      });
      const filas = Object.values(porProv).map(g => ({
        proveedor: g.proveedor, evaluadas: g.evaluadas, aTiempo: g.aTiempo, tarde: g.tarde,
        cumplimiento: g.evaluadas ? pct(g.aTiempo, g.evaluadas) : null,
        atrasoProm: g.atrasos.length ? g.atrasos.reduce((s, a) => s + a, 0) / g.atrasos.length : null,
        pendientesVencidas: g.pendientesVencidas
      })).sort((a, b) => (a.cumplimiento ?? 101) - (b.cumplimiento ?? 101));
      const evalTotal = filas.reduce((s, r) => s + r.evaluadas, 0);
      const okTotal = filas.reduce((s, r) => s + r.aTiempo, 0);
      return {
        kpis: [
          { label: "Entregas evaluadas", valor: String(evalTotal) },
          { label: "Cumplimiento global", valor: evalTotal ? pct(okTotal, evalTotal).toFixed(0) + "%" : "—", color: pct(okTotal, evalTotal) >= 80 ? "#059669" : C.amber },
          { label: "Pendientes vencidas", valor: String(filas.reduce((s, r) => s + r.pendientesVencidas, 0)), color: C.red }
        ],
        columnas: [
          { key: "proveedor", label: "Proveedor", fmt: "texto" },
          { key: "evaluadas", label: "Entregas", fmt: "num" },
          { key: "aTiempo", label: "A tiempo", fmt: "num" },
          { key: "tarde", label: "Tarde", fmt: "num" },
          { key: "cumplimiento", label: "Cumplimiento", fmt: "pct" },
          { key: "atrasoProm", label: "Atraso promedio (días)", fmt: "num" },
          { key: "pendientesVencidas", label: "Vencidas sin recibir", fmt: "num" }
        ],
        filas,
        nota: "Solo se evalúan OCP con fecha de entrega registrada. La recepción real es el primer ingreso de mercancía de la orden."
      };
    }
  },
  {
    id: "ocp-sin-procesar", cat: "compras", icon: "⚠️", titulo: "OCP pendientes de procesar",
    desc: "Órdenes sin ingreso de mercancía o sin causar la factura del proveedor — control interno.",
    filtros: [],
    calc(f) {
      const hoy = today();
      const filas = (state.data.ocp || [])
        .filter(o => enRango(o.fecha, f) && !["cancelada", "borrador"].includes(o.estado))
        .filter(o => !(o._procesado && o._procesado.ingreso) || !(o._procesado && o._procesado.factura))
        .map(o => ({
          numero: o.numero, proveedor: nombreProveedor(o.proveedor_id), fecha: o.fecha,
          dias: diasEntre(o.fecha, hoy), total: o.total || 0,
          ingreso: (o._procesado && o._procesado.ingreso) ? "✅ Sí" : "❌ No",
          causada: (o._procesado && o._procesado.factura) ? "✅ Sí" : "❌ No"
        }))
        .sort((a, b) => b.dias - a.dias);
      return {
        kpis: [
          { label: "OCP pendientes", valor: String(filas.length), color: filas.length ? C.amber : "#059669" },
          { label: "Sin ingreso de mercancía", valor: String(filas.filter(r => r.ingreso === "❌ No").length) },
          { label: "Sin factura causada", valor: String(filas.filter(r => r.causada === "❌ No").length) },
          { label: "Valor pendiente", valor: fmtCOP(filas.reduce((s, r) => s + r.total, 0)) }
        ],
        columnas: [
          { key: "numero", label: "OCP", fmt: "texto" },
          { key: "proveedor", label: "Proveedor", fmt: "texto" },
          { key: "fecha", label: "Fecha", fmt: "fecha" },
          { key: "dias", label: "Días desde emisión", fmt: "dias" },
          { key: "total", label: "Valor", fmt: "cop" },
          { key: "ingreso", label: "Ingreso mercancía", fmt: "texto" },
          { key: "causada", label: "Factura causada", fmt: "texto" }
        ],
        filas
      };
    }
  },
  {
    id: "descuentos-financieros", cat: "compras", icon: "💎", titulo: "Descuentos financieros: capturados vs perdidos",
    desc: "Cuánto se ahorró por pronto pago y cuánto se dejó ir en órdenes pagadas sin descuento.",
    filtros: [],
    calc(f) {
      const filas = [];
      (state.data.ocp || []).filter(o => enRango(o.fecha, f) && o.estado !== "cancelada").forEach(o => {
        const prov = (state.data.proveedores || []).find(p => p.id === o.proveedor_id) || {};
        const condiciones = getDescuentosFinancieros(prov);
        const pagos = o._pagos || [];
        const capturado = pagos.reduce((s, p) => s + (parseNumber(p.descuento) || 0), 0);
        const pagado = pagos.reduce((s, p) => s + (parseNumber(p.monto) || 0), 0);
        if (!pagos.length) return;
        const mejorPct = condiciones.length ? Math.max(...condiciones.map(c => parseNumber(c.pct) || 0)) : 0;
        const perdido = (capturado <= 0 && mejorPct > 0) ? Math.round(pagado * mejorPct / 100) : 0;
        if (capturado <= 0 && perdido <= 0) return;
        filas.push({
          numero: o.numero, proveedor: prov.nombre || "—", fechaPago: pagos[pagos.length - 1].fecha,
          pagado, capturado, perdido,
          condicion: condiciones.length ? condiciones.map(c => `${c.pct}% ${c.medio || ""} ${c.dias ? c.dias + "d" : ""}`.trim()).join(" · ") : "—"
        });
      });
      filas.sort((a, b) => (b.capturado + b.perdido) - (a.capturado + a.perdido));
      return {
        kpis: [
          { label: "Descuento capturado", valor: fmtCOP(filas.reduce((s, r) => s + r.capturado, 0)), color: "#059669" },
          { label: "Oportunidad perdida (est.)", valor: fmtCOP(filas.reduce((s, r) => s + r.perdido, 0)), color: C.red },
          { label: "Órdenes con descuento disponible", valor: String(filas.length) }
        ],
        columnas: [
          { key: "numero", label: "OCP", fmt: "texto" },
          { key: "proveedor", label: "Proveedor", fmt: "texto" },
          { key: "fechaPago", label: "Último pago", fmt: "fecha" },
          { key: "pagado", label: "Pagado", fmt: "cop" },
          { key: "capturado", label: "Descuento capturado", fmt: "cop" },
          { key: "perdido", label: "Oportunidad perdida", fmt: "cop" },
          { key: "condicion", label: "Condición del proveedor", fmt: "texto" }
        ],
        filas,
        nota: "La oportunidad perdida se estima aplicando el mejor % de descuento del proveedor a pagos hechos sin descuento; puede no haber aplicado si el pago fue fuera del plazo."
      };
    }
  }
];

// ============================================================================
// INFORMES — Ventas y Cartera
// ============================================================================
function facturasValidas(f) {
  return (state.data.facturas || []).filter(x => enRango(x.fecha, f) && !["anulada", "borrador"].includes(x.estado));
}

const INF_VENTAS = [
  {
    id: "ventas-cliente", cat: "ventas", icon: "👥", titulo: "Ventas por cliente",
    desc: "Ranking de clientes por ventas netas (descontando notas crédito), con participación y ticket.",
    filtros: [],
    calc(f) {
      const grupos = {};
      facturasValidas(f).forEach(fa => {
        const cid = fa.cliente_id || "sin";
        if (!grupos[cid]) grupos[cid] = { cliente: nombreCliente(fa.cliente_id), n: 0, ventas: 0 };
        grupos[cid].n++;
        grupos[cid].ventas += (fa.total || 0) - getTotalNC(fa.id);
      });
      const total = Object.values(grupos).reduce((s, g) => s + g.ventas, 0);
      const filas = Object.values(grupos).map(g => ({
        cliente: g.cliente, facturas: g.n, ventas: g.ventas,
        ticket: g.n ? g.ventas / g.n : 0, participacion: pct(g.ventas, total)
      })).sort((a, b) => b.ventas - a.ventas);
      return {
        kpis: [
          { label: "Ventas netas", valor: fmtCOP(total), color: "#059669" },
          { label: "Clientes facturados", valor: String(filas.length) },
          { label: "Cliente principal", valor: filas[0] ? `${filas[0].cliente} (${filas[0].participacion.toFixed(0)}%)` : "—" },
          { label: "Concentración top 3", valor: pct(filas.slice(0, 3).reduce((s, r) => s + r.ventas, 0), total).toFixed(0) + "%" }
        ],
        columnas: [
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "facturas", label: "Facturas", fmt: "num" },
          { key: "ventas", label: "Ventas netas", fmt: "cop" },
          { key: "ticket", label: "Ticket promedio", fmt: "cop" },
          { key: "participacion", label: "Participación", fmt: "pct" }
        ],
        filas
      };
    }
  },
  {
    id: "ventas-producto", cat: "ventas", icon: "📦", titulo: "Ventas por producto",
    desc: "Unidades, ventas y margen estimado por artículo facturado en el período.",
    filtros: [],
    calc(f) {
      const grupos = {};
      facturasValidas(f).forEach(fa => {
        (fa.items || []).forEach(it => {
          if (!it.articulo) return;
          if (!grupos[it.articulo]) grupos[it.articulo] = { articulo: it.articulo, unidades: 0, ventas: 0 };
          grupos[it.articulo].unidades += parseNumber(it.cantidad) || 0;
          grupos[it.articulo].ventas += parseNumber(it.total) || 0;
        });
      });
      const filas = Object.values(grupos).map(g => {
        const cpp = getCostoPromedio(g.articulo);
        const costo = cpp * g.unidades;
        return {
          articulo: g.articulo, unidades: g.unidades, ventas: g.ventas,
          precioProm: g.unidades ? g.ventas / g.unidades : 0,
          costo, margen: g.ventas > 0 ? (1 - costo / g.ventas) * 100 : null
        };
      }).sort((a, b) => b.ventas - a.ventas);
      const totalVentas = filas.reduce((s, r) => s + r.ventas, 0);
      return {
        kpis: [
          { label: "Ventas del período", valor: fmtCOP(totalVentas), color: "#059669" },
          { label: "Artículos vendidos", valor: String(filas.length) },
          { label: "Unidades", valor: String(Math.round(filas.reduce((s, r) => s + r.unidades, 0))) }
        ],
        columnas: [
          { key: "articulo", label: "Artículo", fmt: "texto" },
          { key: "unidades", label: "Unidades", fmt: "num" },
          { key: "ventas", label: "Ventas", fmt: "cop" },
          { key: "precioProm", label: "Precio prom.", fmt: "cop" },
          { key: "costo", label: "Costo (CPP)", fmt: "cop" },
          { key: "margen", label: "Margen", fmt: "pct" }
        ],
        filas,
        nota: "El costo usa el costo promedio ponderado actual del kardex — es una aproximación si el CPP cambió después de la venta."
      };
    }
  },
  {
    id: "ventas-vendedor", cat: "ventas", icon: "🧑‍💼", titulo: "Ventas por vendedor",
    desc: "Ventas netas facturadas por asesor comercial en el período, con participación.",
    filtros: [],
    calc(f) {
      const grupos = {};
      facturasValidas(f).forEach(fa => {
        const v = fa.asesor_comercial || "Sin asesor";
        if (!grupos[v]) grupos[v] = { vendedor: v, n: 0, ventas: 0, clientes: new Set() };
        grupos[v].n++;
        grupos[v].ventas += (fa.total || 0) - getTotalNC(fa.id);
        grupos[v].clientes.add(fa.cliente_id);
      });
      const total = Object.values(grupos).reduce((s, g) => s + g.ventas, 0);
      const filas = Object.values(grupos).map(g => ({
        vendedor: g.vendedor, facturas: g.n, clientes: g.clientes.size,
        ventas: g.ventas, participacion: pct(g.ventas, total)
      })).sort((a, b) => b.ventas - a.ventas);
      return {
        kpis: [
          { label: "Ventas netas", valor: fmtCOP(total), color: "#059669" },
          { label: "Vendedores activos", valor: String(filas.filter(r => r.vendedor !== "Sin asesor").length) },
          { label: "Mejor vendedor", valor: filas[0] ? filas[0].vendedor : "—" }
        ],
        columnas: [
          { key: "vendedor", label: "Vendedor", fmt: "texto" },
          { key: "facturas", label: "Facturas", fmt: "num" },
          { key: "clientes", label: "Clientes", fmt: "num" },
          { key: "ventas", label: "Ventas netas", fmt: "cop" },
          { key: "participacion", label: "Participación", fmt: "pct" }
        ],
        filas
      };
    }
  },
  {
    id: "dso-clientes", cat: "ventas", icon: "⏰", titulo: "Comportamiento de pago (DSO)",
    desc: "Días reales que tarda cada cliente en pagar vs su condición pactada.",
    filtros: [],
    calc(f) {
      const grupos = {};
      (state.data.facturas || []).filter(fa => fa.estado === "pagada" && enRango(fa.fecha, f)).forEach(fa => {
        const fechaCobro = fa.fecha_cobro || ((fa._pagos || []).map(p => p.fecha).filter(Boolean).sort().pop());
        if (!fechaCobro) return;
        const cid = fa.cliente_id || "sin";
        if (!grupos[cid]) {
          const cli = (state.data.clientes || []).find(c => c.id === fa.cliente_id) || {};
          grupos[cid] = { cliente: cli.nombre || "Sin cliente", pactado: diasPagoDe(cli, null), dias: [], total: 0 };
        }
        grupos[cid].dias.push(Math.max(0, diasEntre(fa.fecha, fechaCobro)));
        grupos[cid].total += fa.total || 0;
      });
      const filas = Object.values(grupos).map(g => {
        const prom = g.dias.reduce((s, d) => s + d, 0) / g.dias.length;
        return {
          cliente: g.cliente, facturasPagadas: g.dias.length, cobrado: g.total,
          diasProm: prom, pactado: g.pactado,
          desviacion: g.pactado != null ? prom - g.pactado : null
        };
      }).sort((a, b) => (b.desviacion || 0) - (a.desviacion || 0));
      const todosLosDias = Object.values(grupos).flatMap(g => g.dias);
      const dso = todosLosDias.length ? todosLosDias.reduce((s, d) => s + d, 0) / todosLosDias.length : null;
      return {
        kpis: [
          { label: "DSO global", valor: dso != null ? dso.toFixed(0) + " días" : "—", color: dso > 45 ? C.red : dso > 30 ? C.amber : "#059669" },
          { label: "Facturas analizadas", valor: String(todosLosDias.length) },
          { label: "Clientes que exceden lo pactado", valor: String(filas.filter(r => (r.desviacion || 0) > 5).length), color: C.amber }
        ],
        columnas: [
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "facturasPagadas", label: "Facturas pagadas", fmt: "num" },
          { key: "cobrado", label: "Cobrado", fmt: "cop" },
          { key: "diasProm", label: "Días prom. de pago", fmt: "num" },
          { key: "pactado", label: "Pactado", fmt: "dias" },
          { key: "desviacion", label: "Desviación (días)", fmt: "num" }
        ],
        filas,
        nota: "Solo considera facturas ya pagadas en el período. Desviación positiva = el cliente paga más tarde de lo pactado."
      };
    }
  },
  {
    id: "nc-motivo", cat: "ventas", icon: "🧾", titulo: "Notas crédito por motivo",
    desc: "Devoluciones y ajustes agrupados por motivo — la tasa de devoluciones del negocio.",
    filtros: [],
    calc(f) {
      const ncs = (state.data.notas_credito || []).filter(nc => enRango(nc.fecha, f) && nc.estado !== "anulada");
      const ventas = facturasValidas(f).reduce((s, fa) => s + (fa.total || 0), 0);
      const grupos = {};
      ncs.forEach(nc => {
        const m = nc.motivo || "Sin motivo";
        if (!grupos[m]) grupos[m] = { motivo: m, n: 0, total: 0, clientes: new Set() };
        grupos[m].n++; grupos[m].total += nc.total || 0; grupos[m].clientes.add(nc.cliente_id);
      });
      const totalNC = ncs.reduce((s, nc) => s + (nc.total || 0), 0);
      const filas = Object.values(grupos).map(g => ({
        motivo: g.motivo, cantidad: g.n, clientes: g.clientes.size,
        total: g.total, sobreVentas: pct(g.total, ventas)
      })).sort((a, b) => b.total - a.total);
      return {
        kpis: [
          { label: "Notas crédito", valor: String(ncs.length) },
          { label: "Total acreditado", valor: fmtCOP(totalNC), color: C.red },
          { label: "Tasa de devolución", valor: ventas ? pct(totalNC, ventas).toFixed(1) + "%" : "—", color: pct(totalNC, ventas) > 5 ? C.red : "#059669" }
        ],
        columnas: [
          { key: "motivo", label: "Motivo", fmt: "texto" },
          { key: "cantidad", label: "Notas", fmt: "num" },
          { key: "clientes", label: "Clientes", fmt: "num" },
          { key: "total", label: "Total acreditado", fmt: "cop" },
          { key: "sobreVentas", label: "% sobre ventas", fmt: "pct" }
        ],
        filas
      };
    }
  },
  {
    id: "rc-sin-facturar", cat: "ventas", icon: "📮", titulo: "Remisiones entregadas sin facturar",
    desc: "Mercancía ya entregada que aún no se ha facturado — ingresos en riesgo de quedarse por fuera.",
    filtros: [],
    calc() {
      const hoy = today();
      const filas = (state.data.remisiones || [])
        .filter(rc => ["entregada", "completada"].includes(rc.estado))
        .map(rc => ({
          numero: rc.numero, cliente: nombreCliente(rc.cliente_id), fecha: rc.fecha,
          dias: diasEntre(rc.fecha, hoy), opOrigen: rc._origen ? rc._origen.numero : "—",
          // La misma puerta que usa el panel de la lista de remisiones: el
          // precio de la línea de la orden, con el IVA de cada bien según su
          // clasificación (ver core/remisionValor.js).
          valor: valorRemision(rc)
        }))
        .sort((a, b) => b.dias - a.dias);
      return {
        kpis: [
          { label: "RC sin facturar", valor: String(filas.length), color: filas.length ? C.red : "#059669" },
          { label: "Valor estimado (con IVA)", valor: fmtCOP(filas.reduce((s, r) => s + r.valor, 0)), color: C.red },
          { label: "Más antigua", valor: filas.length ? `${filas[0].dias} días` : "—" }
        ],
        columnas: [
          { key: "numero", label: "Remisión", fmt: "texto" },
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "fecha", label: "Entregada", fmt: "fecha" },
          { key: "dias", label: "Días sin facturar", fmt: "dias" },
          { key: "opOrigen", label: "OP origen", fmt: "texto" },
          { key: "valor", label: "Valor estimado", fmt: "cop" }
        ],
        filas,
        nota: "El valor se estima con los precios de la OP de origen, más el IVA de cada bien según su clasificación.",
        cargando: false
      };
    },
    sinRango: true
  },
  {
    id: "anticipos-envejecidos", cat: "ventas", icon: "💵", titulo: "Anticipos sin cruzar",
    desc: "Anticipos de clientes recibidos y aún no aplicados a facturas (pasivo cuenta 2710).",
    filtros: [],
    sinRango: true,
    calc() {
      const hoy = today();
      const filas = [];
      // "Sin cruzar" es tener plata VIVA, y lo que se reporta es esa plata, no el
      // monto recibido: un anticipo aplicado a medias salía por su valor entero.
      // El de SUMATEC (OP-0211) se aplicó a MP155 y le quedaron $49.002 —lo
      // retenido—, y el informe declaraba $2.332.489 de pasivo por cruzar.
      (state.data.pedidos || []).forEach(op => {
        (op._anticipos || []).forEach(a => {
          const vivo = anticipoDisponible(a);
          if (vivo <= 0.01) return;
          filas.push({
            op: op.numero, cliente: nombreCliente(op.cliente_id), fecha: a.fecha,
            dias: diasEntre(a.fecha, hoy), monto: parseNumber(a.monto) || 0,
            aplicado: anticipoAplicado(a), vivo,
            medio: a.medio || "—", referencia: a.referencia || "—"
          });
        });
      });
      filas.sort((a, b) => b.dias - a.dias);
      return {
        kpis: [
          { label: "Anticipos abiertos", valor: String(filas.length) },
          { label: "Pasivo por cruzar", valor: fmtCOP(filas.reduce((s, r) => s + r.vivo, 0)), color: C.amber },
          { label: "Más de 60 días", valor: String(filas.filter(r => r.dias > 60).length), color: C.red }
        ],
        columnas: [
          { key: "op", label: "OP", fmt: "texto" },
          { key: "cliente", label: "Cliente", fmt: "texto" },
          { key: "fecha", label: "Recibido", fmt: "fecha" },
          { key: "dias", label: "Días abierto", fmt: "dias" },
          { key: "monto", label: "Recibido", fmt: "cop" },
          { key: "aplicado", label: "Ya aplicado", fmt: "cop" },
          { key: "vivo", label: "Por cruzar", fmt: "cop" },
          { key: "medio", label: "Medio", fmt: "texto" },
          { key: "referencia", label: "Referencia", fmt: "texto" }
        ],
        filas,
        nota: "Por cruzar = recibido − aplicado a facturas − devuelto al cliente. Es el saldo vivo del pasivo 2710."
      };
    }
  }
];

// ============================================================================
// INFORMES — Tesorería
// ============================================================================
const INF_TESORERIA = [
  {
    id: "flujo-caja", cat: "tesoreria", icon: "🌊", titulo: "Flujo de caja proyectado (8 semanas)",
    desc: "Entradas esperadas (cartera por vencer) vs salidas (CxP por pagar), semana a semana.",
    filtros: [],
    sinRango: true,
    calc() {
      const hoy = today();
      const semanas = [];
      for (let i = 0; i < 8; i++) {
        const ini = new Date(hoy + "T12:00:00"); ini.setDate(ini.getDate() + i * 7);
        const fin = new Date(ini); fin.setDate(fin.getDate() + 6);
        const iso = d => d.toISOString().split("T")[0];
        semanas.push({ ini: iso(ini), fin: iso(fin), entradas: 0, salidas: 0 });
      }
      const ubicar = fecha => {
        if (!fecha || fecha <= hoy) return 0; // vencido → semana actual
        const idx = Math.floor(diasEntre(hoy, fecha) / 7);
        return idx >= 8 ? -1 : idx;
      };
      // Entradas: saldos de facturas emitidas/vencidas por fecha de vencimiento
      (state.data.facturas || []).filter(fa => ["emitida", "vencida"].includes(fa.estado)).forEach(fa => {
        const saldo = getSaldoFactura(fa);
        if (saldo <= 0.01) return;
        const idx = ubicar(fa.fecha_vencimiento);
        if (idx >= 0) semanas[idx].entradas += saldo;
      });
      // Salidas: saldos de OCP causadas no pagadas, vencimiento estimado según días de pago del proveedor
      (state.data.ocp || []).filter(o => o.estado !== "cancelada" && o._procesado && o._procesado.factura && o.estado_pago !== "pagada").forEach(o => {
        const saldo = getSaldoOCP(o);
        if (saldo <= 0.01) return;
        const prov = (state.data.proveedores || []).find(p => p.id === o.proveedor_id) || {};
        const base = (o._procesado && o._procesado.factura_fecha_doc) || o.fecha;
        const dias = diasPagoDe(prov);
        const d = new Date(base + "T12:00:00"); d.setDate(d.getDate() + dias);
        const idx = ubicar(d.toISOString().split("T")[0]);
        if (idx >= 0) semanas[idx].salidas += saldo;
      });
      let acumulado = 0;
      const filas = semanas.map((s, i) => {
        const neto = s.entradas - s.salidas;
        acumulado += neto;
        return {
          semana: i === 0 ? `Semana actual (incluye vencidos)` : `Semana ${i + 1}`,
          rango: `${fmtDate(s.ini)} — ${fmtDate(s.fin)}`,
          entradas: s.entradas, salidas: s.salidas, neto, acumulado
        };
      });
      const totalIn = filas.reduce((s, r) => s + r.entradas, 0);
      const totalOut = filas.reduce((s, r) => s + r.salidas, 0);
      return {
        kpis: [
          { label: "Entradas esperadas", valor: fmtCOP(totalIn), color: "#059669" },
          { label: "Salidas esperadas", valor: fmtCOP(totalOut), color: C.red },
          { label: "Neto 8 semanas", valor: fmtCOP(totalIn - totalOut), color: totalIn - totalOut >= 0 ? "#059669" : C.red }
        ],
        columnas: [
          { key: "semana", label: "Semana", fmt: "texto" },
          { key: "rango", label: "Rango", fmt: "texto" },
          { key: "entradas", label: "Entradas (CxC)", fmt: "cop" },
          { key: "salidas", label: "Salidas (CxP)", fmt: "cop" },
          { key: "neto", label: "Neto", fmt: "cop" },
          { key: "acumulado", label: "Acumulado", fmt: "cop" }
        ],
        filas,
        nota: "Las salidas estiman el vencimiento con los días de pago pactados con cada proveedor (30 si no hay dato). Cartera y CxP vencidas se agrupan en la semana actual."
      };
    }
  }
];

// ============================================================================
// INFORMES — Inventario
// ============================================================================
const INF_INVENTARIO = [
  {
    id: "inv-valorizado", cat: "inventario", icon: "🏷️", titulo: "Inventario valorizado",
    desc: "Stock actual por producto a costo promedio ponderado — el respaldo de la cuenta 1435.",
    filtros: [],
    sinRango: true,
    calc() {
      const filas = (state.data.productos || []).map(p => {
        const stock = getStockProducto(p.nombre);
        const cpp = getCostoPromedio(p.nombre);
        return {
          articulo: p.nombre, categoria: p.categoria || "—", unidad: p.unidad || "—",
          stock, cpp, valor: Math.round(stock * cpp)
        };
      }).sort((a, b) => b.valor - a.valor);
      const total = filas.reduce((s, r) => s + r.valor, 0);
      return {
        kpis: [
          { label: "Valor total inventario", valor: fmtCOP(total), color: NAVY },
          { label: "Productos con stock", valor: String(filas.filter(r => r.stock > 0).length) },
          { label: "Productos sin stock", valor: String(filas.filter(r => r.stock <= 0).length), color: C.amber }
        ],
        columnas: [
          { key: "articulo", label: "Producto", fmt: "texto" },
          { key: "categoria", label: "Categoría", fmt: "texto" },
          { key: "unidad", label: "Unidad", fmt: "texto" },
          { key: "stock", label: "Stock", fmt: "num" },
          { key: "cpp", label: "Costo promedio", fmt: "cop" },
          { key: "valor", label: "Valor", fmt: "cop" }
        ],
        filas,
        nota: "El valor total debe cuadrar con el saldo de la cuenta 1435 (Mercancías)."
      };
    }
  },
  {
    id: "inv-rotacion", cat: "inventario", icon: "🔄", titulo: "Rotación y productos sin movimiento",
    desc: "Días de inventario por producto según las salidas del período, y detección de obsoletos.",
    filtros: [{ key: "diasSinMov", label: "Sin movimiento hace más de (días)", tipo: "number", default: 60 }],
    calc(f) {
      const hoy = today();
      const rangoDias = Math.max(1, diasEntre(f.desde || hoy, f.hasta || hoy));
      const umbral = parseNumber(f.diasSinMov) || 60;
      const filas = (state.data.inventario || []).map(inv => {
        const movs = inv._movimientos || [];
        const salidas = movs.filter(m => m.tipo === "salida" && enRango(m.fecha, f));
        const unidadesVendidas = salidas.reduce((s, m) => s + (parseNumber(m.cantidad) || 0), 0);
        const ultimoMov = movs.map(m => m.fecha).filter(Boolean).sort().pop() || null;
        const stock = parseNumber(inv.stock) || 0;
        const ventaDiaria = unidadesVendidas / rangoDias;
        const diasInventario = ventaDiaria > 0 ? stock / ventaDiaria : null;
        const diasSinMov = ultimoMov ? diasEntre(ultimoMov, hoy) : null;
        return {
          articulo: inv.articulo, stock,
          vendidas: unidadesVendidas, diasInventario,
          ultimoMov, diasSinMov,
          alerta: (stock > 0 && (diasSinMov == null || diasSinMov > umbral)) ? "⚠️ Sin movimiento" : ""
        };
      }).sort((a, b) => (b.diasSinMov || 0) - (a.diasSinMov || 0));
      return {
        kpis: [
          { label: "Productos en kardex", valor: String(filas.length) },
          { label: `Sin movimiento > ${umbral} días (con stock)`, valor: String(filas.filter(r => r.alerta).length), color: C.amber },
          { label: "Unidades vendidas en el período", valor: String(Math.round(filas.reduce((s, r) => s + r.vendidas, 0))) }
        ],
        columnas: [
          { key: "articulo", label: "Producto", fmt: "texto" },
          { key: "stock", label: "Stock", fmt: "num" },
          { key: "vendidas", label: "Salidas del período", fmt: "num" },
          { key: "diasInventario", label: "Días de inventario", fmt: "num" },
          { key: "ultimoMov", label: "Último movimiento", fmt: "fecha" },
          { key: "diasSinMov", label: "Días sin movimiento", fmt: "num" },
          { key: "alerta", label: "Alerta", fmt: "texto" }
        ],
        filas,
        nota: "Días de inventario = stock ÷ venta diaria del período. Vacío = sin ventas en el período."
      };
    }
  },
  {
    id: "inv-cpp-catalogo", cat: "inventario", icon: "⚖️", titulo: "Costo real (CPP) vs costo de catálogo",
    desc: "Dónde el costo real del kardex ya se desvió del costo con que se cotiza — para actualizar precios.",
    filtros: [],
    sinRango: true,
    calc() {
      const filas = (state.data.productos || []).map(p => {
        const cpp = getCostoPromedio(p.nombre);
        const catalogo = parseNumber(p.costo) || 0;
        const precio = parseNumber(p.precio_venta) || 0;
        return {
          articulo: p.nombre, catalogo, cpp,
          diferencia: catalogo > 0 ? ((cpp - catalogo) / catalogo) * 100 : null,
          precioVenta: precio,
          margenReal: precio > 0 ? (1 - cpp / precio) * 100 : null
        };
      }).filter(r => r.cpp > 0 || r.catalogo > 0)
        .sort((a, b) => Math.abs(b.diferencia || 0) - Math.abs(a.diferencia || 0));
      return {
        kpis: [
          { label: "Productos analizados", valor: String(filas.length) },
          { label: "Desviados más de 5%", valor: String(filas.filter(r => Math.abs(r.diferencia || 0) > 5).length), color: C.amber },
          { label: "Con margen real < 20%", valor: String(filas.filter(r => r.margenReal != null && r.margenReal < 20).length), color: C.red }
        ],
        columnas: [
          { key: "articulo", label: "Producto", fmt: "texto" },
          { key: "catalogo", label: "Costo catálogo", fmt: "cop" },
          { key: "cpp", label: "CPP kardex", fmt: "cop" },
          { key: "diferencia", label: "Diferencia", fmt: "pct" },
          { key: "precioVenta", label: "Precio de venta", fmt: "cop" },
          { key: "margenReal", label: "Margen real", fmt: "pct" }
        ],
        filas
      };
    }
  }
];

// ============================================================================
// INFORMES — Contabilidad
// ============================================================================
function tercerosOpciones() {
  const cs = (state.data.clientes || []).map(c => [c.id, `👥 ${c.nombre}`]);
  const ps = (state.data.proveedores || []).map(p => [p.id, `🏭 ${p.nombre}`]);
  return [...cs, ...ps].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
}

const INF_CONTABILIDAD = [
  {
    id: "contab-auxiliar", cat: "contabilidad", icon: "🔎", titulo: "Auxiliar por cuenta y tercero",
    desc: "Movimientos de una cuenta del PUC filtrados por cliente/proveedor, con saldo corrido — para conciliar saldos.",
    filtros: [
      { key: "cuenta", label: "Cuenta PUC", tipo: "select", placeholder: "Selecciona una cuenta", opciones: () => (state.data.puc || []).slice().sort((a, b) => a.codigo.localeCompare(b.codigo)).map(c => [c.codigo, `${c.codigo} — ${c.nombre}`]) },
      { key: "tercero", label: "Tercero", tipo: "select", opciones: tercerosOpciones }
    ],
    calc(f) {
      if (!f.cuenta) {
        return { kpis: [], columnas: [], filas: [], error: "Selecciona una cuenta del PUC en el filtro para generar el auxiliar." };
      }
      const cuentaPUC = (state.data.puc || []).find(c => c.codigo === f.cuenta);
      const naturaleza = cuentaPUC ? cuentaPUC.naturaleza : "D";
      const movs = [];
      (state.data.asientos || []).forEach(a => {
        if (!enRango(a.fecha, f)) return;
        (a.movimientos || []).forEach(m => {
          if (m.cuenta !== f.cuenta) return;
          if (f.tercero && m.tercero_id !== f.tercero) return;
          movs.push({
            fecha: a.fecha, asiento: a.numero, concepto: a.concepto || "",
            origen: a.origen ? `${a.origen.tipo} ${a.origen.numero || ""}`.trim() : "—",
            tercero: m.tercero_id ? (nombreCliente(m.tercero_id) !== "Sin cliente" ? nombreCliente(m.tercero_id) : nombreProveedor(m.tercero_id)) : "—",
            debito: m.debito || 0, credito: m.credito || 0
          });
        });
      });
      movs.sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
      let saldo = 0;
      movs.forEach(m => {
        saldo += naturaleza === "D" ? (m.debito - m.credito) : (m.credito - m.debito);
        m.saldo = saldo;
      });
      const totDeb = movs.reduce((s, m) => s + m.debito, 0);
      const totCred = movs.reduce((s, m) => s + m.credito, 0);
      return {
        kpis: [
          { label: "Débitos", valor: fmtCOP(totDeb) },
          { label: "Créditos", valor: fmtCOP(totCred) },
          { label: "Saldo del período", valor: fmtCOP(saldo), color: NAVY },
          { label: "Movimientos", valor: String(movs.length) }
        ],
        columnas: [
          { key: "fecha", label: "Fecha", fmt: "fecha" },
          { key: "asiento", label: "Asiento", fmt: "texto" },
          { key: "concepto", label: "Concepto", fmt: "texto" },
          { key: "origen", label: "Origen", fmt: "texto" },
          { key: "tercero", label: "Tercero", fmt: "texto" },
          { key: "debito", label: "Débito", fmt: "cop" },
          { key: "credito", label: "Crédito", fmt: "cop" },
          { key: "saldo", label: "Saldo", fmt: "cop" }
        ],
        filas: movs
      };
    }
  },
  {
    id: "contab-comparativo", cat: "contabilidad", icon: "📊", titulo: "Estado de resultados mensual comparativo",
    desc: "Ingresos, costos, gastos y utilidad mes a mes — la tendencia del negocio en una tabla.",
    filtros: [],
    calc(f) {
      const desde = f.desde || rangoDefault().desde;
      const hasta = f.hasta || today();
      const meses = [];
      let cursor = desde.slice(0, 7);
      const fin = hasta.slice(0, 7);
      while (cursor <= fin && meses.length < 24) {
        meses.push(cursor);
        const [y, m] = cursor.split("-").map(Number);
        cursor = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
      }
      const ultimoDia = ym => {
        const [y, m] = ym.split("-").map(Number);
        return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
      };
      const filas = meses.map(ym => {
        const er = calcularEstadoResultados(`${ym}-01`, ultimoDia(ym));
        return {
          mes: nombreMes(ym),
          ingresos: er.ingresosNetos, costo: er.costoVentas, utilidadBruta: er.utilidadBruta,
          gastos: er.gastosOperacionales, utilidadNeta: er.utilidadNeta,
          margenNeto: er.ingresosNetos > 0 ? pct(er.utilidadNeta, er.ingresosNetos) : null
        };
      });
      const tot = filas.reduce((s, r) => ({ i: s.i + r.ingresos, u: s.u + r.utilidadNeta }), { i: 0, u: 0 });
      return {
        kpis: [
          { label: "Ingresos netos del rango", valor: fmtCOP(tot.i), color: "#059669" },
          { label: "Utilidad neta del rango", valor: fmtCOP(tot.u), color: tot.u >= 0 ? "#059669" : C.red },
          { label: "Margen neto promedio", valor: tot.i > 0 ? pct(tot.u, tot.i).toFixed(1) + "%" : "—" }
        ],
        columnas: [
          { key: "mes", label: "Mes", fmt: "texto" },
          { key: "ingresos", label: "Ingresos netos", fmt: "cop" },
          { key: "costo", label: "Costo de ventas", fmt: "cop" },
          { key: "utilidadBruta", label: "Utilidad bruta", fmt: "cop" },
          { key: "gastos", label: "Gastos operacionales", fmt: "cop" },
          { key: "utilidadNeta", label: "Utilidad neta", fmt: "cop" },
          { key: "margenNeto", label: "Margen neto", fmt: "pct" }
        ],
        filas,
        nota: "Cada mes incluye la provisión de renta con la tasa configurada en Contabilidad."
      };
    }
  },
  {
    id: "contab-iva", cat: "contabilidad", icon: "🧾", titulo: "IVA por bimestre",
    desc: "IVA generado vs descontable por bimestre del año — el borrador del Formulario 300.",
    filtros: [{ key: "anio", label: "Año", tipo: "number", default: new Date().getFullYear() }],
    sinRango: true,
    calc(f) {
      const anio = parseInt(f.anio, 10) || new Date().getFullYear();
      const bimestres = [["01", "02"], ["03", "04"], ["05", "06"], ["07", "08"], ["09", "10"], ["11", "12"]];
      const filas = bimestres.map(([m1, m2], i) => {
        const desde = `${anio}-${m1}-01`;
        const hasta = `${anio}-${m2}-${String(new Date(anio, parseInt(m2, 10), 0).getDate()).padStart(2, "0")}`;
        const r = getSaldoCuentaRango("2408", desde, hasta);
        // Las BASES salen de las facturas del bimestre, no de la cuenta 2408:
        // una venta exenta o excluida no deja ningún movimiento de IVA, así que
        // en el mayor es invisible — y el Formulario 300 las pide por separado.
        const delBimestre = (state.data.facturas || []).filter(fa =>
          fa.estado !== "anulada" && fa.fecha >= desde && fa.fecha <= hasta);
        const bases = delBimestre.reduce((acc, fa) => {
          const d = desgloseIVA(fa.items);
          acc.gravadas += d.baseGravada; acc.exentas += d.baseExenta; acc.excluidas += d.baseExcluida;
          return acc;
        }, { gravadas: 0, exentas: 0, excluidas: 0 });
        return {
          bimestre: `Bimestre ${i + 1} (${nombreMes(`${anio}-${m1}`)} – ${nombreMes(`${anio}-${m2}`)})`,
          gravadas: bases.gravadas, exentas: bases.exentas, excluidas: bases.excluidas,
          generado: r.credito, descontable: r.debito,
          saldo: r.credito - r.debito,
          resultado: r.credito - r.debito >= 0 ? "A pagar" : "Saldo a favor"
        };
      });
      return {
        kpis: [
          { label: "IVA generado (año)", valor: fmtCOP(filas.reduce((s, r) => s + r.generado, 0)) },
          { label: "IVA descontable (año)", valor: fmtCOP(filas.reduce((s, r) => s + r.descontable, 0)) },
          { label: "Neto (año)", valor: fmtCOP(filas.reduce((s, r) => s + r.saldo, 0)), color: NAVY }
        ],
        columnas: [
          { key: "bimestre", label: "Bimestre", fmt: "texto" },
          { key: "gravadas", label: "Ventas gravadas", fmt: "cop" },
          { key: "exentas", label: "Ventas exentas", fmt: "cop" },
          { key: "excluidas", label: "Ventas excluidas", fmt: "cop" },
          { key: "generado", label: "IVA generado", fmt: "cop" },
          { key: "descontable", label: "IVA descontable", fmt: "cop" },
          { key: "saldo", label: "Saldo", fmt: "cop" },
          { key: "resultado", label: "Resultado", fmt: "texto" }
        ],
        filas,
        nota: "El IVA sale de la cuenta 2408 (créditos = generado en ventas, débitos = descontable en compras). " +
          "Las bases salen de las facturas: una venta exenta (art. 477) o excluida (art. 424) no mueve la 2408 y " +
          "aun así el Formulario 300 las pide aparte. Si hay ventas excluidas, el IVA descontable de los costos " +
          "comunes se debe prorratear (art. 490). Es un borrador, no reemplaza la declaración."
      };
    }
  }
];

// ============================================================================
// INFORMES — Activos Fijos
// ============================================================================
function depAcumulada(af, campo) {
  const deps = af.depreciaciones || [];
  for (let i = deps.length - 1; i >= 0; i--) {
    if (deps[i][campo] != null) return deps[i][campo];
  }
  return 0;
}

const INF_ACTIVOS = [
  {
    id: "af-listado", cat: "activos", icon: "🏢", titulo: "Activos fijos y valor en libros",
    desc: "Inventario de propiedad, planta y equipo con depreciación acumulada y vida útil restante.",
    filtros: [],
    sinRango: true,
    calc() {
      const filas = (state.data.activos_fijos || []).map(af => {
        const depAcum = depAcumulada(af, "depreciacionAcumuladaContable");
        const costo = parseNumber(af.costo_adquisicion) || 0;
        const valorLibros = Math.max(parseNumber(af.valor_residual) || 0, costo - depAcum);
        const mesesDepreciados = (af.depreciaciones || []).filter(d => d.origen !== "baja").length;
        return {
          codigo: af.codigo, nombre: af.nombre, categoria: af.categoria || "—",
          fechaCompra: af.fecha_compra, costo, depAcum, valorLibros,
          mesesRestantes: af.estado === "dado_de_baja" ? 0 : Math.max(0, (parseNumber(af.vida_util_contable_meses) || 0) - mesesDepreciados),
          responsable: af.responsable || "—",
          estado: af.estado === "dado_de_baja" ? "❌ Dado de baja" : "✅ Activo"
        };
      }).sort((a, b) => b.valorLibros - a.valorLibros);
      return {
        kpis: [
          { label: "Activos registrados", valor: String(filas.length) },
          { label: "Costo total", valor: fmtCOP(filas.reduce((s, r) => s + r.costo, 0)) },
          { label: "Depreciación acumulada", valor: fmtCOP(filas.reduce((s, r) => s + r.depAcum, 0)) },
          { label: "Valor en libros", valor: fmtCOP(filas.filter(r => r.estado === "✅ Activo").reduce((s, r) => s + r.valorLibros, 0)), color: NAVY }
        ],
        columnas: [
          { key: "codigo", label: "Código", fmt: "texto" },
          { key: "nombre", label: "Activo", fmt: "texto" },
          { key: "categoria", label: "Categoría", fmt: "texto" },
          { key: "fechaCompra", label: "Compra", fmt: "fecha" },
          { key: "costo", label: "Costo", fmt: "cop" },
          { key: "depAcum", label: "Dep. acumulada", fmt: "cop" },
          { key: "valorLibros", label: "Valor en libros", fmt: "cop" },
          { key: "mesesRestantes", label: "Meses restantes", fmt: "num" },
          { key: "responsable", label: "Responsable", fmt: "texto" },
          { key: "estado", label: "Estado", fmt: "texto" }
        ],
        filas
      };
    }
  },
  {
    id: "af-conciliacion", cat: "activos", icon: "⚖️", titulo: "Depreciación contable vs fiscal",
    desc: "Diferencia entre la depreciación NIIF y la fiscal (Art. 137 E.T.) por activo — base de la conciliación.",
    filtros: [],
    sinRango: true,
    calc() {
      const filas = (state.data.activos_fijos || []).map(af => {
        const contable = depAcumulada(af, "depreciacionAcumuladaContable");
        const fiscal = depAcumulada(af, "depreciacionAcumuladaFiscal");
        return {
          codigo: af.codigo, nombre: af.nombre,
          vidaContable: parseNumber(af.vida_util_contable_meses) || 0,
          vidaFiscal: parseNumber(af.vida_util_fiscal_meses) || 0,
          contable, fiscal, diferencia: contable - fiscal
        };
      }).sort((a, b) => Math.abs(b.diferencia) - Math.abs(a.diferencia));
      const totC = filas.reduce((s, r) => s + r.contable, 0);
      const totF = filas.reduce((s, r) => s + r.fiscal, 0);
      return {
        kpis: [
          { label: "Dep. acumulada contable", valor: fmtCOP(totC) },
          { label: "Dep. acumulada fiscal", valor: fmtCOP(totF) },
          { label: "Diferencia temporaria", valor: fmtCOP(totC - totF), color: totC - totF !== 0 ? C.amber : "#059669" }
        ],
        columnas: [
          { key: "codigo", label: "Código", fmt: "texto" },
          { key: "nombre", label: "Activo", fmt: "texto" },
          { key: "vidaContable", label: "Vida contable (meses)", fmt: "num" },
          { key: "vidaFiscal", label: "Vida fiscal (meses)", fmt: "num" },
          { key: "contable", label: "Dep. acum. contable", fmt: "cop" },
          { key: "fiscal", label: "Dep. acum. fiscal", fmt: "cop" },
          { key: "diferencia", label: "Diferencia", fmt: "cop" }
        ],
        filas,
        nota: "Diferencia positiva = se ha depreciado más contable que fiscalmente (diferencia temporaria deducible a futuro)."
      };
    }
  },
  {
    id: "af-proyeccion", cat: "activos", icon: "🔮", titulo: "Proyección de depreciación (12 meses)",
    desc: "Gasto mensual de depreciación esperado con los activos actuales.",
    filtros: [],
    sinRango: true,
    calc() {
      const hoy = today();
      const activos = (state.data.activos_fijos || []).filter(af => af.estado !== "dado_de_baja");
      const estadoActivos = activos.map(af => {
        const costo = parseNumber(af.costo_adquisicion) || 0;
        const residual = parseNumber(af.valor_residual) || 0;
        const vida = parseNumber(af.vida_util_contable_meses) || 0;
        const mensual = vida > 0 ? (costo - residual) / vida : 0;
        const depreciados = (af.depreciaciones || []).filter(d => d.origen !== "baja").length;
        return { nombre: af.nombre, mensual, restantes: Math.max(0, vida - depreciados) };
      });
      const filas = [];
      let acumulado = 0;
      let [y, m] = [parseInt(hoy.slice(0, 4), 10), parseInt(hoy.slice(5, 7), 10)];
      for (let i = 0; i < 12; i++) {
        m++; if (m > 12) { m = 1; y++; }
        const ym = `${y}-${String(m).padStart(2, "0")}`;
        let gasto = 0, n = 0;
        estadoActivos.forEach(ea => { if (ea.restantes > i) { gasto += ea.mensual; n++; } });
        acumulado += gasto;
        filas.push({ mes: nombreMes(ym), activos: n, gasto: Math.round(gasto), acumulado: Math.round(acumulado) });
      }
      return {
        kpis: [
          { label: "Gasto mensual actual", valor: fmtCOP(filas[0] ? filas[0].gasto : 0) },
          { label: "Proyección 12 meses", valor: fmtCOP(acumulado), color: NAVY },
          { label: "Activos depreciándose", valor: String(estadoActivos.filter(ea => ea.restantes > 0).length) }
        ],
        columnas: [
          { key: "mes", label: "Mes", fmt: "texto" },
          { key: "activos", label: "Activos depreciándose", fmt: "num" },
          { key: "gasto", label: "Gasto proyectado", fmt: "cop" },
          { key: "acumulado", label: "Acumulado", fmt: "cop" }
        ],
        filas,
        nota: "Proyección en línea recta con los activos vigentes; no incluye compras ni bajas futuras."
      };
    }
  }
];

// ============================================================================
// INFORMES — Comisiones y Control (auditoría)
// ============================================================================
const INF_CONTROL = [
  {
    id: "comisiones-historico", cat: "control", icon: "🏆", titulo: "Histórico mensual de comisiones",
    desc: "Comisiones aplicadas como costo a las OP, mes a mes y por vendedor.",
    filtros: [{ key: "vendedor", label: "Vendedor", tipo: "select", opciones: vendedoresOpciones }],
    calc(f) {
      const grupos = {};
      (state.data.pedidos || []).forEach(op => {
        (op._conceptosCosto || []).filter(cc => cc._esComision).forEach(cc => {
          const fecha = cc._fecha || op.fecha;
          if (!enRango(fecha, f)) return;
          const vendedor = cc._vendedor || op.asesor_comercial || "Sin vendedor";
          if (f.vendedor && vendedor !== f.vendedor) return;
          const clave = `${mesDe(fecha)}|${vendedor}`;
          if (!grupos[clave]) grupos[clave] = { ym: mesDe(fecha), vendedor, n: 0, total: 0 };
          grupos[clave].n++;
          grupos[clave].total += (parseNumber(cc.cantidad) || 0) * (parseNumber(cc.valor_tercero) || 0);
        });
      });
      const filas = Object.values(grupos)
        .sort((a, b) => b.ym.localeCompare(a.ym) || b.total - a.total)
        .map(g => ({ mes: nombreMes(g.ym), vendedor: g.vendedor, ops: g.n, total: g.total }));
      return {
        kpis: [
          { label: "Total comisiones", valor: fmtCOP(filas.reduce((s, r) => s + r.total, 0)) },
          { label: "OPs con comisión", valor: String(filas.reduce((s, r) => s + r.ops, 0)) }
        ],
        columnas: [
          { key: "mes", label: "Mes", fmt: "texto" },
          { key: "vendedor", label: "Vendedor", fmt: "texto" },
          { key: "ops", label: "OPs", fmt: "num" },
          { key: "total", label: "Comisión aplicada", fmt: "cop" }
        ],
        filas,
        nota: "Solo incluye comisiones ya aplicadas como costo desde el módulo de Comisiones."
      };
    }
  },
  {
    id: "uso-sistema", cat: "control", icon: "⏱️", titulo: "Tiempo de uso del sistema",
    desc: "Horas reales de trabajo en el ERP por usuario y día — clics y teclado, descontando inactividad.",
    filtros: [],
    calc(f) {
      const clave = `uso|${f.desde || ""}|${f.hasta || ""}`;
      const cache = state._infUsoCache;
      if (!cache || cache.clave !== clave) {
        if (state._infUsoPendiente !== clave) {
          state._infUsoPendiente = clave;
          cargarUsoDiario(f.desde || "", f.hasta || "").then(rows => {
            state._infUsoCache = { clave, rows };
            state._infUsoPendiente = null;
            render();
          });
        }
        return { kpis: [], columnas: [], filas: [], cargando: true };
      }
      const fmtHoras = seg => {
        const h = Math.floor(seg / 3600), m = Math.round((seg % 3600) / 60);
        return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
      };
      const fmtHora = ts => {
        if (!ts) return "—";
        try { return new Date(ts).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit", timeZone: "America/Bogota" }); } catch { return "—"; }
      };
      const filas = cache.rows.map(r => ({
        fecha: r.fecha,
        usuario: r.usuario_nombre || r.usuario_email || r.usuario_id,
        tiempoActivo: fmtHoras(r.segundos_activos || 0),
        _seg: r.segundos_activos || 0,
        primera: fmtHora(r.primera_actividad),
        ultima: fmtHora(r.ultima_actividad)
      }));
      // Promedio diario por usuario (sobre los días registrados)
      const porUsuario = {};
      filas.forEach(r => {
        if (!porUsuario[r.usuario]) porUsuario[r.usuario] = { total: 0, dias: 0 };
        porUsuario[r.usuario].total += r._seg;
        porUsuario[r.usuario].dias++;
      });
      const usuarios = Object.entries(porUsuario);
      const totalSeg = filas.reduce((s, r) => s + r._seg, 0);
      return {
        kpis: [
          { label: "Usuarios con registro", valor: String(usuarios.length) },
          { label: "Tiempo total del rango", valor: fmtHoras(totalSeg) },
          ...usuarios.slice(0, 2).map(([u, d]) => ({
            label: `Promedio diario · ${u}`, valor: fmtHoras(d.total / d.dias), sub: `${d.dias} día${d.dias !== 1 ? "s" : ""} con actividad`
          }))
        ],
        columnas: [
          { key: "fecha", label: "Fecha", fmt: "fecha" },
          { key: "usuario", label: "Usuario", fmt: "texto" },
          { key: "tiempoActivo", label: "Tiempo activo", fmt: "texto" },
          { key: "primera", label: "Primera actividad", fmt: "texto" },
          { key: "ultima", label: "Última actividad", fmt: "texto" }
        ],
        filas,
        nota: "Cuenta solo tiempo con actividad real (clics, teclado, movimiento); tras 5 minutos sin actividad o con la pestaña oculta deja de sumar. Horas de Colombia. Los usuarios no admin solo ven su propio registro."
      };
    }
  },
  {
    id: "auditoria-actividad", cat: "control", icon: "🕵️", titulo: "Actividad por usuario (auditoría)",
    desc: "Quién crea, edita y elimina registros en el sistema, con su última actividad.",
    filtros: [],
    calc(f) {
      const clave = `${f.desde || ""}|${f.hasta || ""}`;
      const cache = state._infAudCache;
      if (!cache || cache.clave !== clave) {
        if (state._infAudPendiente !== clave) {
          state._infAudPendiente = clave;
          cargarAuditoria({ entidad: "", usuario: "", desde: f.desde || "", hasta: f.hasta || "" }).then(rows => {
            state._infAudCache = { clave, rows };
            state._infAudPendiente = null;
            render();
          });
        }
        return { kpis: [], columnas: [], filas: [], cargando: true };
      }
      const grupos = {};
      cache.rows.forEach(r => {
        const u = r.usuario_nombre || r.usuario_email || "Desconocido";
        if (!grupos[u]) grupos[u] = { usuario: u, total: 0, creados: 0, editados: 0, eliminados: 0, ultima: "" };
        const g = grupos[u];
        g.total++;
        if (r.accion === "create") g.creados++;
        else if (r.accion === "update") g.editados++;
        else if (r.accion === "delete") g.eliminados++;
        if (r.fecha > g.ultima) g.ultima = r.fecha;
      });
      const filas = Object.values(grupos).map(g => ({
        ...g, ultima: g.ultima ? g.ultima.slice(0, 10) : null
      })).sort((a, b) => b.total - a.total);
      return {
        kpis: [
          { label: "Eventos registrados", valor: String(cache.rows.length), sub: "máx. 200 más recientes" },
          { label: "Usuarios activos", valor: String(filas.length) },
          { label: "Eliminaciones", valor: String(filas.reduce((s, r) => s + r.eliminados, 0)), color: filas.some(r => r.eliminados) ? C.amber : "#059669" }
        ],
        columnas: [
          { key: "usuario", label: "Usuario", fmt: "texto" },
          { key: "total", label: "Acciones", fmt: "num" },
          { key: "creados", label: "Creados", fmt: "num" },
          { key: "editados", label: "Editados", fmt: "num" },
          { key: "eliminados", label: "Eliminados", fmt: "num" },
          { key: "ultima", label: "Última actividad", fmt: "fecha" }
        ],
        filas,
        nota: "Basado en los últimos 200 eventos del log de auditoría para el rango elegido. El detalle completo está en el módulo Auditoría."
      };
    }
  }
];

// ============================================================================
// INFORMES — Nómina y Empleados
// ============================================================================
function liquidacionesOpciones() {
  return (state.data.nomina_liquidaciones || []).slice()
    .sort((a, b) => (b.periodo_hasta || "").localeCompare(a.periodo_hasta || ""))
    .map(l => [l.id, `${l.numero} · ${fmtDate(l.periodo_desde)} — ${fmtDate(l.periodo_hasta)}${l.estado === "borrador" ? " (borrador)" : ""}`]);
}

function getLiquidacionNomina(f) {
  const liqs = state.data.nomina_liquidaciones || [];
  if (f.liquidacion) return liqs.find(l => l.id === f.liquidacion) || null;
  return liqs.slice().sort((a, b) => (b.periodo_hasta || "").localeCompare(a.periodo_hasta || ""))[0] || null;
}

function antiguedadTexto(fechaIngreso) {
  if (!fechaIngreso) return "—";
  const dias = diasEntre(fechaIngreso, today());
  const anios = Math.floor(dias / 365);
  const meses = Math.floor((dias % 365) / 30);
  return anios > 0 ? `${anios} año${anios !== 1 ? "s" : ""} ${meses} mes${meses !== 1 ? "es" : ""}` : `${meses} mes${meses !== 1 ? "es" : ""}`;
}

const INF_NOMINA = [
  {
    id: "nomina-resumen", cat: "nomina", icon: "🧾", titulo: "Resumen de liquidación de nómina",
    desc: "Devengados, deducciones, neto y costo total empresa por empleado, con desprendible imprimible.",
    filtros: [{ key: "liquidacion", label: "Liquidación", tipo: "select", placeholder: "Última liquidación", opciones: liquidacionesOpciones }],
    sinRango: true,
    filaAcciones: fila => `<button data-action="inf-desprendible" data-liq="${fila._liqId}" data-emp="${fila._empId}" style="${btnStyle(NAVY, null, true)}">🧾 Desprendible</button>`,
    calc(f) {
      const liq = getLiquidacionNomina(f);
      if (!liq) return { kpis: [], columnas: [], filas: [], error: "No hay liquidaciones de nómina registradas." };
      const filas = (liq.detalle_empleados || []).map(d => ({
        _liqId: liq.id, _empId: d.empleadoId,
        empleado: d.empleadoNombre, cargo: d.empleadoCargo || "—",
        devengado: d.devengado.totalDevengado, deducciones: d.deducciones.totalDeducciones,
        neto: d.netoPagar,
        aportes: d.aportesPatronales.totalAportesPatronales,
        provisiones: d.provisionPrestaciones.totalProvisionPrestaciones,
        costoEmpresa: d.devengado.totalDevengado + d.aportesPatronales.totalAportesPatronales + d.provisionPrestaciones.totalProvisionPrestaciones
      }));
      return {
        kpis: [
          { label: "Liquidación", valor: liq.numero, sub: `${fmtDate(liq.periodo_desde)} — ${fmtDate(liq.periodo_hasta)}` },
          { label: "Neto a pagar", valor: fmtCOP(filas.reduce((s, r) => s + r.neto, 0)), color: "#059669" },
          { label: "Costo total empresa", valor: fmtCOP(filas.reduce((s, r) => s + r.costoEmpresa, 0)), color: NAVY },
          { label: "Empleados", valor: String(filas.length) }
        ],
        columnas: [
          { key: "empleado", label: "Empleado", fmt: "texto" },
          { key: "cargo", label: "Cargo", fmt: "texto" },
          { key: "devengado", label: "Devengado", fmt: "cop" },
          { key: "deducciones", label: "Deducciones", fmt: "cop" },
          { key: "neto", label: "Neto a pagar", fmt: "cop" },
          { key: "aportes", label: "Aportes patronales", fmt: "cop" },
          { key: "provisiones", label: "Provisiones", fmt: "cop" },
          { key: "costoEmpresa", label: "Costo empresa", fmt: "cop" }
        ],
        filas
      };
    }
  },
  {
    id: "nomina-aportes", cat: "nomina", icon: "🏥", titulo: "Aportes por entidad (base PILA)",
    desc: "Salud, pensión, ARL y parafiscales por empleado y entidad — para cruzar contra la planilla PILA.",
    filtros: [{ key: "liquidacion", label: "Liquidación", tipo: "select", placeholder: "Última liquidación", opciones: liquidacionesOpciones }],
    sinRango: true,
    calc(f) {
      const liq = getLiquidacionNomina(f);
      if (!liq) return { kpis: [], columnas: [], filas: [], error: "No hay liquidaciones de nómina registradas." };
      const filas = (liq.detalle_empleados || []).map(d => {
        const emp = (state.data.empleados || []).find(e => e.id === d.empleadoId) || {};
        return {
          empleado: d.empleadoNombre, ibc: d.devengado.ibc,
          eps: emp.eps || "—", salud: d.deducciones.saludEmpleado + d.aportesPatronales.saludPatronal,
          fondo: emp.fondo_pensiones || "—", pension: d.deducciones.pensionEmpleado + d.aportesPatronales.pensionPatronal + d.deducciones.fondoSolidaridad,
          arlEntidad: emp.arl || "—", arl: d.aportesPatronales.arl,
          sena: d.aportesPatronales.sena, icbf: d.aportesPatronales.icbf, caja: d.aportesPatronales.cajaCompensacion
        };
      });
      const sum = k => filas.reduce((s, r) => s + (r[k] || 0), 0);
      return {
        kpis: [
          { label: "IBC total", valor: fmtCOP(sum("ibc")) },
          { label: "Salud", valor: fmtCOP(sum("salud")) },
          { label: "Pensión", valor: fmtCOP(sum("pension")) },
          { label: "ARL + parafiscales", valor: fmtCOP(sum("arl") + sum("sena") + sum("icbf") + sum("caja")) }
        ],
        columnas: [
          { key: "empleado", label: "Empleado", fmt: "texto" },
          { key: "ibc", label: "IBC", fmt: "cop" },
          { key: "eps", label: "EPS", fmt: "texto" },
          { key: "salud", label: "Salud (emp+pat)", fmt: "cop" },
          { key: "fondo", label: "Fondo pensiones", fmt: "texto" },
          { key: "pension", label: "Pensión (emp+pat+FSP)", fmt: "cop" },
          { key: "arlEntidad", label: "ARL", fmt: "texto" },
          { key: "arl", label: "Aporte ARL", fmt: "cop" },
          { key: "sena", label: "SENA", fmt: "cop" },
          { key: "icbf", label: "ICBF", fmt: "cop" },
          { key: "caja", label: "Caja comp.", fmt: "cop" }
        ],
        filas,
        nota: "Los valores agrupan aporte del empleado + aporte patronal según la liquidación. No reemplaza la PILA oficial."
      };
    }
  },
  {
    id: "nomina-provisiones", cat: "nomina", icon: "🏦", titulo: "Provisiones de prestaciones acumuladas",
    desc: "Cesantías, intereses, prima y vacaciones provisionadas por empleado en el rango.",
    filtros: [],
    calc(f) {
      const grupos = {};
      (state.data.nomina_liquidaciones || [])
        .filter(l => l.estado === "confirmada" && enRango(l.periodo_hasta, f))
        .forEach(l => {
          (l.detalle_empleados || []).forEach(d => {
            if (!grupos[d.empleadoId]) grupos[d.empleadoId] = { empleado: d.empleadoNombre, cesantias: 0, intereses: 0, prima: 0, vacaciones: 0, total: 0 };
            const g = grupos[d.empleadoId];
            const p = d.provisionPrestaciones || {};
            g.cesantias += p.cesantias || 0;
            g.intereses += p.interesesCesantias || 0;
            g.prima += p.prima || 0;
            g.vacaciones += p.vacaciones || 0;
            g.total += p.totalProvisionPrestaciones || 0;
          });
        });
      const filas = Object.values(grupos).sort((a, b) => b.total - a.total);
      return {
        kpis: [
          { label: "Total provisionado", valor: fmtCOP(filas.reduce((s, r) => s + r.total, 0)), color: NAVY },
          { label: "Cesantías + intereses", valor: fmtCOP(filas.reduce((s, r) => s + r.cesantias + r.intereses, 0)) },
          { label: "Prima", valor: fmtCOP(filas.reduce((s, r) => s + r.prima, 0)) },
          { label: "Vacaciones", valor: fmtCOP(filas.reduce((s, r) => s + r.vacaciones, 0)) }
        ],
        columnas: [
          { key: "empleado", label: "Empleado", fmt: "texto" },
          { key: "cesantias", label: "Cesantías", fmt: "cop" },
          { key: "intereses", label: "Int. cesantías", fmt: "cop" },
          { key: "prima", label: "Prima", fmt: "cop" },
          { key: "vacaciones", label: "Vacaciones", fmt: "cop" },
          { key: "total", label: "Total", fmt: "cop" }
        ],
        filas,
        nota: "Suma solo liquidaciones confirmadas cuyo período termina dentro del rango."
      };
    }
  },
  {
    id: "nomina-cert-ingresos", cat: "nomina", icon: "📄", titulo: "Ingresos y retenciones por empleado (anual)",
    desc: "Base del certificado anual de ingresos y retenciones — con PDF por empleado.",
    filtros: [{ key: "anio", label: "Año", tipo: "number", default: new Date().getFullYear() }],
    sinRango: true,
    filaAcciones: fila => `<button data-action="inf-cert-ingresos" data-emp="${fila._empId}" data-anio="${fila._anio}" style="${btnStyle(NAVY, null, true)}">📄 Certificado</button>`,
    calc(f) {
      const anio = String(parseInt(f.anio, 10) || new Date().getFullYear());
      const grupos = {};
      (state.data.nomina_liquidaciones || [])
        .filter(l => l.estado === "confirmada" && (l.periodo_hasta || "").startsWith(anio))
        .forEach(l => {
          (l.detalle_empleados || []).forEach(d => {
            if (!grupos[d.empleadoId]) grupos[d.empleadoId] = { _empId: d.empleadoId, _anio: anio, empleado: d.empleadoNombre, devengado: 0, salud: 0, pension: 0, fondoSol: 0, neto: 0, liquidaciones: 0 };
            const g = grupos[d.empleadoId];
            g.devengado += d.devengado.totalDevengado;
            g.salud += d.deducciones.saludEmpleado;
            g.pension += d.deducciones.pensionEmpleado;
            g.fondoSol += d.deducciones.fondoSolidaridad || 0;
            g.neto += d.netoPagar;
            g.liquidaciones++;
          });
        });
      const filas = Object.values(grupos).sort((a, b) => b.devengado - a.devengado);
      return {
        kpis: [
          { label: `Devengado ${anio}`, valor: fmtCOP(filas.reduce((s, r) => s + r.devengado, 0)) },
          { label: "Empleados", valor: String(filas.length) },
          { label: "Liquidaciones incluidas", valor: String(filas.reduce((s, r) => Math.max(s, r.liquidaciones), 0)) }
        ],
        columnas: [
          { key: "empleado", label: "Empleado", fmt: "texto" },
          { key: "liquidaciones", label: "Liquidaciones", fmt: "num" },
          { key: "devengado", label: "Total devengado", fmt: "cop" },
          { key: "salud", label: "Aporte salud", fmt: "cop" },
          { key: "pension", label: "Aporte pensión", fmt: "cop" },
          { key: "fondoSol", label: "Fondo solidaridad", fmt: "cop" },
          { key: "neto", label: "Neto pagado", fmt: "cop" }
        ],
        filas,
        nota: "Suma liquidaciones confirmadas del año elegido. El PDF es un soporte informativo, no reemplaza el formato 220 de la DIAN."
      };
    }
  },
  {
    id: "empleados-listado", cat: "nomina", icon: "🧑‍💼", titulo: "Planta de personal y antigüedad",
    desc: "Listado exportable de empleados con antigüedad, afiliaciones y datos de pago, con certificado laboral.",
    filtros: [],
    sinRango: true,
    filaAcciones: fila => `<button data-action="inf-cert-laboral" data-emp="${fila._empId}" style="${btnStyle(NAVY, null, true)}">📄 Cert. laboral</button>`,
    calc() {
      const filas = (state.data.empleados || [])
        .filter(e => !e.fecha_retiro)
        .map(e => ({
          _empId: e.id,
          nombre: e.nombre, cargo: e.cargo || "—", cedula: e.cedula || "—",
          ingreso: e.fecha_ingreso, antiguedad: antiguedadTexto(e.fecha_ingreso),
          salario: parseNumber(e.salario_base) || 0,
          eps: e.eps || "—", fondo: e.fondo_pensiones || "—",
          arl: e.arl ? `${e.arl}${e.nivel_riesgo_arl ? " (nivel " + e.nivel_riesgo_arl + ")" : ""}` : "—",
          banco: e.banco ? `${e.banco}${e.cuenta_bancaria ? " · " + e.cuenta_bancaria : ""}` : "—"
        }))
        .sort((a, b) => (a.ingreso || "").localeCompare(b.ingreso || ""));
      return {
        kpis: [
          { label: "Empleados activos", valor: String(filas.length) },
          { label: "Nómina base mensual", valor: fmtCOP(filas.reduce((s, r) => s + r.salario, 0)) },
          { label: "Antigüedad promedio", valor: filas.length ? (filas.reduce((s, r) => s + diasEntre(r.ingreso || today(), today()), 0) / filas.length / 365).toFixed(1) + " años" : "—" }
        ],
        columnas: [
          { key: "nombre", label: "Empleado", fmt: "texto" },
          { key: "cargo", label: "Cargo", fmt: "texto" },
          { key: "cedula", label: "Cédula", fmt: "texto" },
          { key: "ingreso", label: "Ingreso", fmt: "fecha" },
          { key: "antiguedad", label: "Antigüedad", fmt: "texto" },
          { key: "salario", label: "Salario base", fmt: "cop" },
          { key: "eps", label: "EPS", fmt: "texto" },
          { key: "fondo", label: "Fondo pensiones", fmt: "texto" },
          { key: "arl", label: "ARL", fmt: "texto" },
          { key: "banco", label: "Banco / cuenta", fmt: "texto" }
        ],
        filas
      };
    }
  }
];

// ============================================================================
// REGISTRO DE INFORMES
// ============================================================================
const INFORMES = [
  ...INF_COMERCIAL,
  ...INF_PRODUCCION,
  ...INF_COMPRAS,
  ...INF_VENTAS,
  ...INF_TESORERIA,
  ...INF_INVENTARIO,
  ...INF_CONTABILIDAD,
  ...INF_ACTIVOS,
  ...INF_NOMINA,
  ...INF_CONTROL
];

const getInforme = id => INFORMES.find(r => r.id === id);

// ============================================================================
// FRAMEWORK DE RENDERIZADO
// ============================================================================
function ensureFiltros(rep) {
  if (!state.informesFiltros || state.informesFiltros._rep !== rep.id) {
    const base = { _rep: rep.id, ...rangoDefault() };
    (rep.filtros || []).forEach(fl => { if (fl.default != null) base[fl.key] = String(fl.default); });
    state.informesFiltros = base;
  }
  return state.informesFiltros;
}

function filtrosHTML(rep, f) {
  const rango = rep.sinRango ? "" : `
    <div>
      <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Desde</label>
      <input type="date" value="${f.desde || ""}" style="${inputStyle}width:150px;" onchange="state.informesFiltros.desde=this.value;render();"/>
    </div>
    <div>
      <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">Hasta</label>
      <input type="date" value="${f.hasta || ""}" style="${inputStyle}width:150px;" onchange="state.informesFiltros.hasta=this.value;render();"/>
    </div>
  `;
  const extras = (rep.filtros || []).map(fl => {
    if (fl.tipo === "select") {
      const ops = fl.opciones();
      return `
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">${fl.label}</label>
          <select style="${inputStyle}min-width:170px;" onchange="state.informesFiltros['${fl.key}']=this.value;render();">
            <option value="">${fl.placeholder || "Todos"}</option>
            ${ops.map(([v, l]) => `<option value="${escapeHTML(String(v))}" ${String(f[fl.key]) === String(v) ? "selected" : ""}>${escapeHTML(String(l))}</option>`).join("")}
          </select>
        </div>`;
    }
    if (fl.tipo === "number") {
      return `
        <div>
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:3px;">${fl.label}</label>
          <input type="number" min="0" value="${escapeHTML(f[fl.key] != null ? String(f[fl.key]) : "")}" style="${inputStyle}width:110px;" onchange="state.informesFiltros['${fl.key}']=this.value;render();"/>
        </div>`;
    }
    return "";
  }).join("");
  const exportBtn = `<button data-action="inf-export" style="${btnStyle("#16a34a")}margin-left:auto;">📥 Exportar Excel</button>`;
  if (!rango && !extras) {
    return `<div style="margin-bottom:10px;display:flex;">${exportBtn}</div>`;
  }
  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:10px;display:flex;gap:9px;flex-wrap:wrap;align-items:flex-end;">
      ${rango}${extras}
      ${exportBtn}
    </div>
  `;
}

function kpisHTML(kpis) {
  if (!kpis || !kpis.length) return "";
  return `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:9px;margin-bottom:10px;">
      ${kpis.map(k => `
        <div style="background:white;border:1px solid ${C.border};border-left:4px solid ${k.color || NAVY};border-radius:6px;padding:13px 15px;">
          <div style="font-size:10px;color:${C.muted};font-weight:700;letter-spacing:.4px;text-transform:uppercase;">${escapeHTML(k.label)}</div>
          <div style="font-size:19px;font-weight:800;color:${k.color || C.text};margin-top:3px;">${k.valor}</div>
          ${k.sub ? `<div style="font-size:10.5px;color:${C.muted};margin-top:2px;">${escapeHTML(k.sub)}</div>` : ""}
        </div>
      `).join("")}
    </div>
  `;
}

function tablaHTML(rep, resultado) {
  const { columnas, filas } = resultado;
  if (!filas.length) {
    return `<div style="padding:22px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;color:${C.muted};font-size:12px;">Sin datos para los filtros seleccionados.</div>`;
  }
  const ths = columnas.map(col => {
    const fmt = FMT[col.fmt] || FMT.texto;
    return `<th style="padding:6px 9px;text-align:${fmt.align};font-size:10px;color:${C.muted};font-weight:700;letter-spacing:.4px;text-transform:uppercase;border-bottom:2px solid ${NAVY}20;white-space:nowrap;">${escapeHTML(col.label)}</th>`;
  }).join("") + (rep.filaAcciones ? "<th></th>" : "");
  const trs = filas.map(fila => {
    const tds = columnas.map(col => {
      const fmt = FMT[col.fmt] || FMT.texto;
      return `<td style="padding:6px 10px;text-align:${fmt.align};font-size:11.5px;color:${C.text};">${fmt.render(fila[col.key], fila)}</td>`;
    }).join("");
    const acciones = rep.filaAcciones ? `<td style="padding:6px 12px;text-align:right;white-space:nowrap;">${rep.filaAcciones(fila)}</td>` : "";
    return `<tr style="border-top:1px solid ${C.border};">${tds}${acciones}</tr>`;
  }).join("");
  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;min-width:720px;">
        <thead><tr style="background:#f8fafc;">${ths}</tr></thead>
        <tbody>${trs}</tbody>
      </table>
    </div>
    <div style="font-size:10.5px;color:${C.muted};margin-top:8px;">${filas.length} fila${filas.length !== 1 ? "s" : ""}${resultado.nota ? ` · 💡 ${escapeHTML(resultado.nota)}` : ""}</div>
  `;
}

function renderInforme(rep) {
  const catInfo = CATEGORIAS.find(c => c.id === rep.cat) || {};
  const f = ensureFiltros(rep);
  let resultado;
  try {
    resultado = rep.calc(f);
  } catch (err) {
    console.error(`Error calculando informe ${rep.id}:`, err);
    resultado = { kpis: [], columnas: [], filas: [], error: err.message };
  }
  const cuerpo = resultado.cargando
    ? `<div style="padding:22px;text-align:center;background:white;border:1px solid ${C.border};border-radius:6px;color:${C.muted};font-size:12px;">⏳ Cargando datos…</div>`
    : resultado.error
      ? `<div style="padding:11px;background:#fef2f2;border:1px solid #fecaca;border-radius:6px;color:#b91c1c;font-size:12px;">⚠️ Error generando el informe: ${escapeHTML(resultado.error)}</div>`
      : kpisHTML(resultado.kpis) + tablaHTML(rep, resultado);
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:18px;flex-wrap:wrap;gap:9px;">
      <div>
        <div style="font-size:10.5px;color:${catInfo.color || C.muted};font-weight:700;letter-spacing:.6px;">${catInfo.icon || ""} ${(catInfo.label || "").toUpperCase()}</div>
        <h2 style="margin:2px 0 0;font-size:17px;font-weight:800;color:${C.text};">${rep.icon} ${escapeHTML(rep.titulo)}</h2>
        <div style="font-size:11.5px;color:${C.muted};margin-top:2px;">${escapeHTML(rep.desc)}</div>
      </div>
      <button data-action="inf-volver" style="${btnStyle(null, "secondary")}">← Todos los informes</button>
    </div>
    ${filtrosHTML(rep, f)}
    ${cuerpo}
  `;
}

function renderHub() {
  const grupos = CATEGORIAS.map(cat => {
    const reps = INFORMES.filter(r => r.cat === cat.id);
    if (!reps.length) return "";
    return `
      <div style="margin-bottom:22px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:12px;">${cat.icon}</span>
          <span style="font-size:11.5px;font-weight:800;letter-spacing:.8px;color:${cat.color};">${cat.label.toUpperCase()}</span>
          <span style="flex:1;height:2px;background:linear-gradient(90deg,${cat.color}50,transparent);border-radius:2px;"></span>
        </div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:10px;">
          ${reps.map(r => `
            <button data-action="inf-abrir" data-rep="${r.id}" style="text-align:left;background:white;border:1px solid ${C.border};border-radius:6px;padding:13px 15px;cursor:pointer;font-family:inherit;transition:all .15s;border-left:4px solid ${cat.color};">
              <div style="font-size:12px;font-weight:700;color:${C.text};">${r.icon} ${escapeHTML(r.titulo)}</div>
              <div style="font-size:10.5px;color:${C.muted};margin-top:4px;line-height:1.45;">${escapeHTML(r.desc)}</div>
            </button>
          `).join("")}
        </div>
      </div>
    `;
  }).join("");
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
      <div>
        <h2 style="margin:0;font-size:16px;font-weight:800;color:${NAVY};">📑 Informes</h2>
        <div style="font-size:11.5px;color:${C.muted};margin-top:3px;">${INFORMES.length} informes gerenciales organizados por proceso. Todos exportables a Excel.</div>
      </div>
    </div>
    ${grupos}
  `;
}

export function renderInformes() {
  const rep = state.informesRep ? getInforme(state.informesRep) : null;
  return rep ? renderInforme(rep) : renderHub();
}

// ============================================================================
// ACCIONES
// ============================================================================
import { render } from "../ui/app.js";
import { registerActions } from "../ui/actions.js";

registerActions({
  "inf-abrir": (d) => {
    state.informesRep = d.rep;
    state.informesFiltros = null;
    render();
  },
  "inf-volver": () => {
    state.informesRep = null;
    state.informesFiltros = null;
    render();
  },
  "inf-export": () => {
    const rep = state.informesRep ? getInforme(state.informesRep) : null;
    if (!rep) return;
    const f = ensureFiltros(rep);
    let resultado;
    try { resultado = rep.calc(f); } catch (err) { alert("⚠️ Error generando el informe: " + err.message); return; }
    if (resultado.cargando) { alert("⏳ Los datos aún se están cargando, intenta de nuevo en unos segundos."); return; }
    const cols = resultado.columnas.filter(c => c.fmt !== "html");
    const encabezados = cols.map(c => c.label);
    const filas = resultado.filas.map(fila => cols.map(c => (FMT[c.fmt] || FMT.texto).csv(fila[c.key], fila)));
    csvDescargar(`${BRAND.archivo_prefijo}_Informe_${rep.id}_${today()}`, encabezados, filas);
  },
  "inf-desprendible": (d) => { exportDesprendibleNomina(d.liq, d.emp); },
  "inf-cert-laboral": (d) => { exportCertificadoLaboral(d.emp); },
  "inf-cert-ingresos": (d) => { exportCertIngresosRetenciones(d.emp, d.anio); }
});
